import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { Button, Card, CardContent, Input, Field, FieldLabel, FieldError, Drawer } from '../../../../components/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { useAuthStore } from '../../../../store/auth';
import { useCurrentAcademy, getUserAcademiesFromCache } from '../../../../hooks/useDataStore';
import {
  Lesson,
  lessonFormSchema,
  doTimesOverlap,
  findCourtHireBookingsConflictingWithLessonSchedule,
  getLessonCourtIds,
  getLessonCoachIds,
  type LessonFormData,
  TierFeatureKey,
  type StudentProfile,
  type Coach,
  type Court,
  hasAcademyManagementRole,
  shouldWarnWhenEditingLessonInRunningSchedule,
  formatDateToFormYmd,
  parseLocalDateYmd,
  type LessonCreateRequest,
  computeCampWeekSessionDates,
} from '@court-sync/shared';
import { PrivatePackageSessionDatesField } from '../../../../components/lessons/PrivatePackageSessionDatesField';
import {
  CampLessonPricingFields,
  CampLessonScheduleFields,
} from '../../../../components/lessons/CampLessonFormFields';
import { campSessionDatesToExplicitSlots, useCampWeekSchedule } from '@/utils/campLessonForm';
import { formatSchedulingErrorMessage } from '@/utils/schedulingErrors';
import { useLessons, useCourts, useTerms, useCoaches, useLocations } from '../../../../hooks/useDataStore';
import { useAgeGroupLookups } from '../../../../queries/ageGroups';
import { AcademyScheduleCalendar } from '@/components/schedule/AcademyScheduleCalendar';
import {
  DayCourtScheduleView,
  type DayCourtReschedulePayload,
} from '@/components/schedule/DayCourtScheduleView';
import {
  DayColumnAxisToggle,
  persistDayColumnAxis,
  readStoredDayColumnAxis,
  type DayScheduleColumnAxis,
} from '@/components/schedule/DayColumnAxisToggle';
import { commitDayScheduleDrag } from '@/utils/dayScheduleMutations';
import { SessionRescheduleNotifyDialog } from '@/components/schedule/SessionRescheduleNotifyDialog';
import {
  LessonCalendarMonthNav,
  LessonScheduleSubheaderToolbar,
  ScheduleDayNav,
  ScheduleWeekNav,
} from '@/components/schedule/ScheduleToolbar';
import { DashboardEmptyStateCard, DashboardEmptyStateIcon } from '@/components/dashboard/DashboardEmptyState';
import { getCurrencyFromRegion, getCurrencySymbol, formatCurrency, type Currency } from '../../../../utils/currency';
import { useSubscriptionCheck } from '../../../../hooks/useSubscriptionCheck';
import { useManagementDashboardFeatureAccess } from '../../../../hooks/useFeatureAccess';
import { useHasMultiLocation } from '../../../../hooks/useHasMultiLocation';

import { ArrowUp, ArrowDown, CalendarDays, CalendarClock, List, Calendar as CalendarIcon, MoreVertical, Pencil, Trash2, Link2, AlertTriangle, Loader2, Layers, ChevronDown, Copy } from 'lucide-react';
import {
  QuickLessonCreateForm,
  type QuickLessonFormDetailLevel,
} from '@/components/lessons/QuickLessonCreateForm';
import { PostCreateLessonStep } from '@/components/lessons/PostCreateLessonStep';
import {
  buildQuickCreateLessonFormValues,
  buildDuplicateLessonFormValues,
  canQuickDuplicateLesson,
} from '@/utils/quickLessonForm';
import { PageHeader } from '@/components/layout/PageHeader';
import type { OnboardingEmbeddedPageProps } from '@/components/onboarding/onboardingEmbeddedPage';
import { ACADEMY_PAGE_TOUR_STEP_IDS } from '@/lib/tour-constants';
import { PageFeatureHelp } from '@/components/tour/PageFeatureHelp';
import { useAutoPageTour } from '@/hooks/useAutoPageTour';
import { CalendarFiltersDropdown } from '@/components/schedule/CalendarFiltersDropdown';
import { ScheduleSourceTypeFilterBar } from '@/components/schedule/ScheduleSourceTypeFilterBar';
import { useCurrentClub } from '@/hooks/useCurrentClub';
import { useFacilityClub } from '@/hooks/useFacilityClub';
import { useCourtHireBookingsInRange } from '@/hooks/useCourtHireBookingsInRange';
import { useFacilityScheduleLayer } from '@/hooks/useFacilityScheduleLayer';
import { useScheduleSourceTypeAccess } from '@/hooks/useScheduleSourceTypeAccess';
import { useScheduleSourceTypeFilters } from '@/hooks/useScheduleSourceTypeFilters';
import ClubOnlyManagementCalendar from '@/pages/dashboards/club/ClubOnlyManagementCalendar';
import { PostponeLessonModal, type OccurrenceDateOption } from '@/components/lessons/PostponeLessonModal';
import { lessonOverrideService } from '@/services/lessonOverride';
import { useLessonCalendarStore } from '@/store/lessonCalendar';
import { lessonService } from '@/services/lesson';
import { coachService } from '@/services/coach';
import { generateLessonOccurrences } from '@court-sync/shared';
import { PaginatedList, type PaginationState } from '@/components/ui';
import { UserAvatar } from '@/components/UserAvatar';
import { cn } from '@/lib/utils';
import { lessonDescriptionTextClassName } from '@/utils/lessonDisplay';
import { formatTimeHHMM } from '@/utils/timeFormat';
import {
  computeLessonAvailabilityWarningDays,
  getCoachAvailabilityMissingDays,
} from '@/utils/coachAvailabilityWarnings';
import {
  isLessonIncludedOnAcademyCalendar,
  type LessonOccurrenceClickPayload,
  type ScheduleActiveStatusFilter,
  buildAcademyOccurrencesFromLessonSessions,
  scheduleOccurrenceToClickPayload,
  isScheduleOccurrenceInDateRange,
  type ScheduleOccurrence,
} from '@/utils/scheduleOccurrences';
import { getSessionRescheduleConfirmMeta } from '@/utils/sessionRescheduleUi';
import { formatInTimeZone } from 'date-fns-tz';
import { LessonSessionDetailModal } from '@/components/lessons/LessonSessionDetailModal';
import { PlayEventScheduleDetailDrawer } from '@/components/play/PlayEventScheduleDetailDrawer';
import { LessonEditRunningScheduleWarningDialog } from '@/components/lessons/LessonEditRunningScheduleWarningDialog';
import { LessonCoverImageField } from '@/components/lessons/LessonCoverImageField';
import { uploadLessonImage } from '@/services/storage';
import { useCoverImageUpload } from '@/hooks/useCoverImageUpload';
import { useProgramLessonTemplate } from '@/hooks/useProgramLessonTemplate';
import { academyStudentService } from '@/services/academyStudentService';
import { usePrograms } from '@/hooks/useDataStore';
import { useAcademySports } from '@/queries/academySports';
import { AcademySportSelectField } from '@/components/sports/AcademySportSelectField';
import { AgeGroupCalendarLegend, ScheduleTypeStatusLegend } from '@/components/sports/AgeGroupCalendarLegend';
import { resolvePrimaryAcademySportId, getAcademySportLabel, isMultiSportAcademy } from '@/utils/sportPresentation';
import {
  defaultLessonCalendarFilterSnapshot,
  readLessonCalendarFilterSnapshot,
  writeLessonCalendarFilterSnapshot,
} from '@/utils/lessonCalendarFilterStorage';
import {
  addDays,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

const DAYS_OF_WEEK = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
];

function defaultLessonFilterYear(): string {
  return String(new Date().getFullYear());
}

function lessonFiltersForActiveStatus(
  activeFilter: string
): { activeStatus?: 'active' | 'inactive' } {
  // Camp instances keep isActive=true when their camp group is deactivated — client-side
  // camp group checks handle inactive/all; only active uses a narrower API fetch.
  if (activeFilter === 'active') return { activeStatus: 'active' };
  return {};
}

function toScheduleActiveStatusFilter(activeFilter: string): ScheduleActiveStatusFilter {
  if (activeFilter === 'inactive' || activeFilter === 'all') return activeFilter;
  return 'active';
}

function lessonMatchesLocationFilter(lesson: Lesson, locationId: string, courts: Court[]): boolean {
  if (lesson.locationId) return lesson.locationId === locationId;
  const courtIds = getLessonCourtIds(lesson);
  return courtIds.some((cid) => courts.find((c) => c.id === cid)?.locationId === locationId);
}

/** API / Lesson date → YYYY-MM-DD for <input type="date"> (local calendar day). */
function lessonDateToFormString(value: unknown): string {
  if (value == null) return '';
  if (value instanceof Date) return formatDateToFormYmd(value);
  if (typeof value === 'string') return formatDateToFormYmd(value);
  const v = value as { toDate?: () => Date; seconds?: number; _seconds?: number };
  if (v.toDate && typeof v.toDate === 'function') {
    return formatDateToFormYmd(v.toDate());
  }
  if (typeof v.seconds === 'number') {
    return formatDateToFormYmd(new Date(v.seconds * 1000));
  }
  if (typeof v._seconds === 'number') {
    return formatDateToFormYmd(new Date(v._seconds * 1000));
  }
  return '';
}

/** Parse lesson date fields (API / Firestore) to a local start-of-day Date, or null. */
function lessonDateFieldToLocalDate(value: unknown): Date | null {
  if (value == null) return null;
  let d: Date | null = null;
  if (value instanceof Date) {
    d = new Date(value);
  } else if (typeof value === 'string') {
    d = parseLocalDateYmd(value) ?? new Date(value);
  } else {
    const v = value as { toDate?: () => Date; seconds?: number; _seconds?: number };
    if (v.toDate && typeof v.toDate === 'function') {
      d = v.toDate();
    } else if (typeof v.seconds === 'number') {
      d = new Date(v.seconds * 1000);
    } else if (typeof v._seconds === 'number') {
      d = new Date(v._seconds * 1000);
    } else {
      return null;
    }
  }
  if (!d || Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
}

/** True when [campStart, campEnd] overlaps the calendar year (local dates). */
function campDateRangeOverlapsYear(campStart: Date, campEnd: Date, selectedYear: number): boolean {
  const yearStart = new Date(selectedYear, 0, 1);
  yearStart.setHours(0, 0, 0, 0);
  const yearEnd = new Date(selectedYear, 11, 31);
  yearEnd.setHours(0, 0, 0, 0);
  const cs = new Date(campStart);
  const ce = new Date(campEnd);
  cs.setHours(0, 0, 0, 0);
  ce.setHours(0, 0, 0, 0);
  return ce >= yearStart && cs <= yearEnd;
}

/** DB/API may return HH:MM:SS; form + schema expect HH:MM */
function normalizeTimeForForm(t: string | undefined): string {
  if (!t) return '';
  const m = t.match(/^(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : t;
}

function normalizeLessonProgramId(programId: LessonFormData['programId']): string | null {
  return programId && String(programId).trim().length > 0 ? programId : null;
}

function sortPrivateSessionDates(dates: string[] | undefined): string[] {
  return [...(dates ?? [])].filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d)).sort();
}

function privateSessionDatesToExplicitSlots(
  dates: string[],
  startTime: string,
  endTime: string
): Array<{ sessionDate: string; startTime: string; endTime: string }> {
  return sortPrivateSessionDates(dates).map((sessionDate) => ({
    sessionDate,
    startTime,
    endTime,
  }));
}

function weekdaysFromPrivateSessionDates(dates: string[]): number[] {
  const dows = new Set<number>();
  for (const d of sortPrivateSessionDates(dates)) {
    const parsed = parseLocalDateYmd(d);
    if (parsed) dows.add(parsed.getDay());
  }
  const arr = [...dows].sort((a, b) => a - b);
  return arr.length > 0 ? arr : [1];
}

function buildEmptyLessonFormValues(): LessonFormData {
  return {
    lessonType: 'group',
    daysOfWeek: [1],
    maxCapacity: 6,
    price: 50,
    campDayPrice: 45,
    campFullPrice: 200,
    campSessionDates: [],
    isActive: true,
    isFeatured: false,
    termIds: [],
    privateSessionDates: [],
    startDate: '',
    endDate: '',
    numberOfSessions: 10,
    pricingType: 'total',
    courtIds: [],
    coachIds: [],
    name: '',
    description: '',
    skillLevel: 'any',
    ageGroup: 'all',
    startTime: '',
    endTime: '',
    locationId: '',
    imageUrl: '',
    programId: null,
    academySportId: undefined,
  };
}

function campFormDefaultsForLessonType(lessonType: LessonFormData['lessonType']): Partial<LessonFormData> {
  if (lessonType === 'camp') {
    return {
      daysOfWeek: [1, 2, 3, 4, 5],
      maxCapacity: 12,
      price: 200,
      campDayPrice: 45,
      campFullPrice: 200,
      campSessionDates: [],
      startDate: '',
      endDate: '',
      termIds: [],
      pricingType: 'total',
    };
  }
  if (lessonType === 'private') {
    return {
      daysOfWeek: [1],
      maxCapacity: 1,
      price: 50,
      termIds: [],
      privateSessionDates: [],
      startDate: '',
      endDate: '',
      numberOfSessions: 10,
    };
  }
  return {
    daysOfWeek: [1],
    maxCapacity: 6,
    price: 50,
    termIds: [],
    startDate: '',
    endDate: '',
  };
}


/** Terms that have not ended (end date is today or later, local calendar day). */
function isTermSelectableForLessonForm(term: { endDate: Date | string }): boolean {
  const end = new Date(term.endDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return end >= today;
}

type ScheduleViewMode = 'list' | 'monthly' | 'weekly' | 'day';

const LESSON_SCHEDULE_VIEW_STORAGE_KEY = 'courtsync.lessonManagement.scheduleView';

function readStoredLessonScheduleView(): ScheduleViewMode {
  try {
    const stored = localStorage.getItem(LESSON_SCHEDULE_VIEW_STORAGE_KEY);
    if (stored === 'list' || stored === 'monthly' || stored === 'weekly' || stored === 'day') {
      return stored;
    }
  } catch {
    /* localStorage unavailable */
  }
  return 'monthly';
}

function persistLessonScheduleView(view: ScheduleViewMode): void {
  try {
    localStorage.setItem(LESSON_SCHEDULE_VIEW_STORAGE_KEY, view);
  } catch {
    /* localStorage unavailable */
  }
}

const SCHEDULE_VIEW_OPTIONS: {
  value: ScheduleViewMode;
  label: string;
  icon: typeof CalendarIcon;
}[] = [
  { value: 'monthly', label: 'Monthly', icon: CalendarIcon },
  { value: 'weekly', label: 'Weekly', icon: CalendarClock },
  { value: 'day', label: 'Day', icon: CalendarDays },
  { value: 'list', label: 'List', icon: List },
];

/** Non-Radix view switcher — dropdown UX without Radix ref loops. */
function ScheduleViewDropdown({
  value,
  onChange,
  triggerClassName,
}: {
  value: ScheduleViewMode;
  onChange: (value: ScheduleViewMode) => void;
  triggerClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const active = SCHEDULE_VIEW_OPTIONS.find((opt) => opt.value === value) ?? SCHEDULE_VIEW_OPTIONS[0];
  const ActiveIcon = active.icon;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      const el = containerRef.current;
      if (el && !el.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        aria-label="Change schedule view"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex h-8 items-center gap-1.5 px-2.5 sm:px-3 ${triggerClassName ?? ''}`}
      >
        <ActiveIcon className="h-4 w-4 shrink-0" aria-hidden />
        <span className="hidden sm:inline">{active.label}</span>
        <ChevronDown className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
      </Button>
      {open ? (
        <ul
          className="absolute right-0 top-full z-50 mt-1 min-w-[10rem] overflow-hidden rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md"
          role="listbox"
          aria-label="Schedule view"
        >
          {SCHEDULE_VIEW_OPTIONS.map(({ value: optionValue, label, icon: Icon }) => {
            const selected = value === optionValue;
            return (
              <li key={optionValue} role="option" aria-selected={selected}>
                <button
                  type="button"
                  className={`flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground ${
                    selected ? 'bg-accent/60 font-medium' : ''
                  }`}
                  onClick={() => {
                    onChange(optionValue);
                    setOpen(false);
                  }}
                >
                  <Icon className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                  {label}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

export default function Calendar({ embedded = false }: OnboardingEmbeddedPageProps = {}) {
  const sourceAccess = useScheduleSourceTypeAccess();
  if (sourceAccess.isClubOnlyOperator) {
    return <ClubOnlyManagementCalendar embedded={embedded} />;
  }
  return <AcademyManagementCalendar embedded={embedded} />;
}

function AcademyManagementCalendar({ embedded = false }: OnboardingEmbeddedPageProps = {}) {
  const { user } = useAuthStore();
  const { currentAcademy, loadCurrentAcademy, loadUserAcademies } = useCurrentAcademy();
  const { currentClub } = useCurrentClub();
  const { facilityClub } = useFacilityClub(currentAcademy);
  const sourceAccess = useScheduleSourceTypeAccess();
  const { activeSourceFilters, toggleSource, isSourceActive } = useScheduleSourceTypeFilters(
    sourceAccess.availableSourceTypes
  );
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasValidSubscription, tooltipMessage } = useSubscriptionCheck();
  const { hasAccess: hasPaymentProcessingAccess } = useManagementDashboardFeatureAccess(TierFeatureKey.PAYMENT_PROCESSING);
  const { hasAccess: hasProgramsAccess } = useManagementDashboardFeatureAccess(TierFeatureKey.PROGRAMS);
  const { hasAccess: hasSmsNotificationsAccess } = useManagementDashboardFeatureAccess(
    TierFeatureKey.SMS_NOTIFICATIONS
  );
  const smsAvailable = hasSmsNotificationsAccess && (currentAcademy?.smsEnabled ?? false);
  const hasMultiLocation = useHasMultiLocation();
  const { locations } = useLocations(currentAcademy?.id, {
    includeInactive: true,
    enabled: hasMultiLocation,
  });
  const { sports: academySports } = useAcademySports(currentAcademy?.id);
  const defaultAcademySportId = resolvePrimaryAcademySportId(academySports);

  const [selectedActiveFilter, setSelectedActiveFilter] = useState<string>('active');
  const lessonListFilters = useMemo(
    () => lessonFiltersForActiveStatus(selectedActiveFilter),
    [selectedActiveFilter]
  );

  // Use centralized stores
  const {
    lessons: allLessons,
    isLoading: lessonsLoading,
    createLesson,
    updateLesson,
    deleteLesson,
    fetchLessons: fetchLessonsForFilter,
  } = useLessons(currentAcademy?.id, lessonListFilters);
  const { courts, isLoading: courtsLoading } = useCourts(currentAcademy?.id);
  const courtNamesById = useMemo(
    () => Object.fromEntries(courts.map((c) => [c.id, c.name] as const)),
    [courts]
  );
  const { coaches, isLoading: coachesLoading, refresh: refreshCoaches } = useCoaches(currentAcademy?.id);
  const { terms, isLoading: termsLoading } = useTerms(currentAcademy?.id);

  const [coachesHydrated, setCoachesHydrated] = useState(false);
  const [coachFilterResultsOpen, setCoachFilterResultsOpen] = useState(false);
  const [studentFilterResultsOpen, setStudentFilterResultsOpen] = useState(false);
  const coachFilterWrapRef = useRef<HTMLDivElement>(null);
  const studentFilterWrapRef = useRef<HTMLDivElement>(null);

  const ensureCoachesForLessonUi = useCallback(async () => {
    if (!currentAcademy?.id) return;
    try {
      await refreshCoaches();
    } catch {
      /* non-fatal */
    } finally {
      setCoachesHydrated(true);
    }
  }, [currentAcademy?.id, refreshCoaches]);

  useEffect(() => {
    setCoachesHydrated(false);
  }, [currentAcademy?.id]);

  useEffect(() => {
    if (!coachFilterResultsOpen) return undefined;
    const onDoc = (e: MouseEvent) => {
      const el = coachFilterWrapRef.current;
      if (el && !el.contains(e.target as Node)) setCoachFilterResultsOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [coachFilterResultsOpen]);

  useEffect(() => {
    if (!studentFilterResultsOpen) return undefined;
    const onDoc = (e: MouseEvent) => {
      const el = studentFilterWrapRef.current;
      if (el && !el.contains(e.target as Node)) setStudentFilterResultsOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [studentFilterResultsOpen]);

  // Filter lessons by academy (store already filters, but keep for safety)
  // Memoize to prevent infinite loops in useEffect dependencies
  const lessons = useMemo(() => {
    return allLessons.filter(lesson => lesson.academyId === currentAcademy?.id);
  }, [allLessons, currentAcademy?.id]);

  const termsForLessonForm = useMemo(
    () => terms.filter((term) => isTermSelectableForLessonForm(term)),
    [terms]
  );

  const hasActiveCoaches = useMemo(() => coaches.some((c) => c.isActive), [coaches]);
  const missingCoachesForSetup = coachesHydrated && !hasActiveCoaches;

  const loading = lessonsLoading || courtsLoading || termsLoading;
  const [submitting, setSubmitting] = useState(false);
  const [editingLesson, setEditingLesson] = useState<Lesson | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [campWeekCount, setCampWeekCount] = useState(1);
  const [formDetailLevel, setFormDetailLevel] = useState<QuickLessonFormDetailLevel>('quick');
  const [postCreateLesson, setPostCreateLesson] = useState<{
    id: string;
    name: string;
    maxCapacity?: number;
  } | null>(null);
  const [postCreateDrawerFooter, setPostCreateDrawerFooter] = useState<React.ReactNode | null>(null);
  const [selectedTermFilter, setSelectedTermFilter] = useState<string>('all');
  const [selectedYearFilter, setSelectedYearFilter] = useState<string>(defaultLessonFilterYear);
  const [selectedLessonTypeFilter, setSelectedLessonTypeFilter] = useState<string>('all');
  const [selectedSkillLevelFilter, setSelectedSkillLevelFilter] = useState<string>('all');
  const [selectedAgeGroupFilter, setSelectedAgeGroupFilter] = useState<string>('all');
  const [selectedLocationFilter, setSelectedLocationFilter] = useState<string>('all');
  const [coachFilterSelection, setCoachFilterSelection] = useState<{
    ids: string[];
    meta: Record<string, Coach>;
  }>({ ids: [], meta: {} });
  const [selectedCourtFilter, setSelectedCourtFilter] = useState<string>('all');
  const [selectedSportFilter, setSelectedSportFilter] = useState<string>('all');
  const [studentFilterSelection, setStudentFilterSelection] = useState<{
    ids: string[];
    meta: Record<string, StudentProfile>;
  }>({ ids: [], meta: {} });
  const [studentEnrolledLessonIds, setStudentEnrolledLessonIds] = useState<Set<string> | null>(null);
  const [studentPickerStudents, setStudentPickerStudents] = useState<StudentProfile[]>([]);
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [studentSearchDebounced, setStudentSearchDebounced] = useState('');
  const [studentSearchLoading, setStudentSearchLoading] = useState(false);
  const [coachSearchQuery, setCoachSearchQuery] = useState('');
  const [coachSearchDebounced, setCoachSearchDebounced] = useState('');
  const [coachPickerCoaches, setCoachPickerCoaches] = useState<Coach[]>([]);
  const [coachSearchLoading, setCoachSearchLoading] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'type' | 'price' | 'day' | 'time' | 'enrollment' | 'term'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [lessonFiltersReady, setLessonFiltersReady] = useState(false);
  const [viewMode, setViewModeState] = useState<ScheduleViewMode>(readStoredLessonScheduleView);
  const setViewMode = useCallback((view: ScheduleViewMode) => {
    setViewModeState(view);
    persistLessonScheduleView(view);
  }, []);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const [calendarWeekStart, setCalendarWeekStart] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [selectedDayDate, setSelectedDayDate] = useState(() => new Date());
  const [dayColumnAxis, setDayColumnAxisState] = useState<DayScheduleColumnAxis>(readStoredDayColumnAxis);
  const setDayColumnAxis = useCallback((axis: DayScheduleColumnAxis) => {
    setDayColumnAxisState(axis);
    persistDayColumnAxis(axis);
  }, []);
  const [pendingDayReschedule, setPendingDayReschedule] = useState<DayCourtReschedulePayload | null>(null);
  const [dayRescheduleSaving, setDayRescheduleSaving] = useState(false);
  const [_listPage, setListPage] = useState(1);
  const LIST_PAGE_SIZE = 20;
  const [listPagination, setListPagination] = useState<PaginationState | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [pagedLessons, setPagedLessons] = useState<Lesson[]>([]);

  useEffect(() => {
    const academyId = currentAcademy?.id;
    if (!academyId) {
      setLessonFiltersReady(false);
      return;
    }

    setLessonFiltersReady(false);
    const stored = readLessonCalendarFilterSnapshot(academyId, defaultLessonFilterYear());
    if (stored) {
      setSelectedYearFilter(stored.year);
      setSelectedTermFilter(stored.term);
      setSelectedLessonTypeFilter(stored.lessonType);
      setSelectedSkillLevelFilter(stored.skillLevel);
      setSelectedAgeGroupFilter(stored.ageGroup);
      setSelectedLocationFilter(stored.location);
      setSelectedCourtFilter(stored.court);
      setSelectedSportFilter(stored.sport);
      setSelectedActiveFilter(stored.activeStatus);
      setCoachFilterSelection({ ids: stored.coachIds, meta: {} });
      setStudentFilterSelection({ ids: stored.studentIds, meta: {} });
      setSortBy(stored.sortBy);
      setSortOrder(stored.sortOrder);
    }
    setLessonFiltersReady(true);
  }, [currentAcademy?.id]);

  useEffect(() => {
    const academyId = currentAcademy?.id;
    if (!academyId || !lessonFiltersReady) return;

    writeLessonCalendarFilterSnapshot(academyId, {
      year: selectedYearFilter,
      term: selectedTermFilter,
      lessonType: selectedLessonTypeFilter,
      skillLevel: selectedSkillLevelFilter,
      ageGroup: selectedAgeGroupFilter,
      location: selectedLocationFilter,
      court: selectedCourtFilter,
      sport: selectedSportFilter,
      activeStatus: selectedActiveFilter,
      coachIds: coachFilterSelection.ids,
      studentIds: studentFilterSelection.ids,
      sortBy,
      sortOrder,
    });
  }, [
    currentAcademy?.id,
    lessonFiltersReady,
    selectedYearFilter,
    selectedTermFilter,
    selectedLessonTypeFilter,
    selectedSkillLevelFilter,
    selectedAgeGroupFilter,
    selectedLocationFilter,
    selectedCourtFilter,
    selectedSportFilter,
    selectedActiveFilter,
    coachFilterSelection.ids,
    studentFilterSelection.ids,
    sortBy,
    sortOrder,
  ]);

  const facilityRange = useMemo(() => {
    if (viewMode === 'day') {
      return { start: startOfDay(selectedDayDate), end: endOfDay(selectedDayDate) };
    }
    if (viewMode === 'weekly') {
      return {
        start: calendarWeekStart,
        end: endOfDay(addDays(calendarWeekStart, 6)),
      };
    }
    const monthStart = startOfMonth(calendarMonth);
    const monthEnd = endOfMonth(calendarMonth);
    return {
      start: startOfWeek(monthStart),
      end: endOfWeek(monthEnd),
    };
  }, [viewMode, selectedDayDate, calendarWeekStart, calendarMonth]);

  const facilityClubId = facilityClub?.id ?? currentClub?.id;
  const organizationId = currentClub?.organizationId ?? currentAcademy?.organizationId;

  const facilityLayer = useFacilityScheduleLayer({
    clubId: facilityClubId,
    rangeStart: facilityRange.start,
    rangeEnd: facilityRange.end,
    includeCourtHire: sourceAccess.canShowCourtHire,
    includeClosures: sourceAccess.canShowClosures,
    includeOccupancyLessons: false,
    includePlayEvents: sourceAccess.canShowPlayEvents,
    organizationId,
    enabled:
      (sourceAccess.hasFacilityLayer || sourceAccess.canShowPlayEvents) &&
      !!(facilityClubId || organizationId),
  });

  const facilityExtraOccurrences = useMemo(
    () => [
      ...facilityLayer.courtHireOccurrences,
      ...facilityLayer.closureOccurrences,
      ...facilityLayer.playEventOccurrences,
    ],
    [
      facilityLayer.courtHireOccurrences,
      facilityLayer.closureOccurrences,
      facilityLayer.playEventOccurrences,
    ]
  );

  const listCourtHireOccurrences = useMemo(
    () =>
      isSourceActive('court_hire')
        ? facilityLayer.courtHireOccurrences.filter((occ) =>
            isScheduleOccurrenceInDateRange(occ, facilityRange.start, facilityRange.end)
          )
        : [],
    [facilityLayer.courtHireOccurrences, facilityRange, isSourceActive]
  );

  const listClosureOccurrences = useMemo(
    () =>
      isSourceActive('closure')
        ? facilityLayer.closureOccurrences.filter((occ) =>
            isScheduleOccurrenceInDateRange(occ, facilityRange.start, facilityRange.end)
          )
        : [],
    [facilityLayer.closureOccurrences, facilityRange, isSourceActive]
  );

  const listPlayEventOccurrences = useMemo(
    () =>
      isSourceActive('play_event')
        ? facilityLayer.playEventOccurrences.filter((occ) =>
            isScheduleOccurrenceInDateRange(occ, facilityRange.start, facilityRange.end)
          )
        : [],
    [facilityLayer.playEventOccurrences, facilityRange, isSourceActive]
  );
  const [conflicts, setConflicts] = useState<Array<{
    type: 'court' | 'coach';
    lesson: Lesson;
    termName: string;
    courtName: string;
    coachName: string;
  }>>([]);
  const [showPostponeModal, setShowPostponeModal] = useState(false);
  const [lessonForPostpone, setLessonForPostpone] = useState<Lesson | null>(null);
  const [postponeOccurrenceDates, setPostponeOccurrenceDates] = useState<OccurrenceDateOption[]>([]);
  const [postponeOriginalDate, setPostponeOriginalDate] = useState<string | null>(null);
  const [postponeLoading, setPostponeLoading] = useState(false);
  const [availabilityWarnings, setAvailabilityWarnings] = useState<Array<{
    coachId: string;
    coachName: string;
    missingDays: number[];
  }>>([]);
  const [courtHireWarnings, setCourtHireWarnings] = useState<Array<{
    courtName: string;
    bookingDate: string;
    bookingTime: string;
  }>>([]);
  const lessonSessions = useLessonCalendarStore((state) => state.lessonSessions);
  const overrides = useLessonCalendarStore((state) => state.overrides);
  const makeupSessions = useLessonCalendarStore((state) => state.makeupSessions);
  const schedulePendingBookings = useLessonCalendarStore((state) => state.schedulePendingBookings);
  const scheduleEnrollmentBookings = useLessonCalendarStore((state) => state.scheduleEnrollmentBookings);
  const removedFromSessionEnrollments = useLessonCalendarStore(
    (state) => state.removedFromSessionEnrollments
  );
  const reportedAbsenceSessions = useLessonCalendarStore((state) => state.reportedAbsenceSessions);
  const fetchOwnerScheduleForRange = useLessonCalendarStore((state) => state.fetchOwnerScheduleForRange);
  const refreshOwnerScheduleAfterMutation = useLessonCalendarStore(
    (state) => state.refreshOwnerScheduleAfterMutation
  );
  const [sessionDetailOccurrence, setSessionDetailOccurrence] = useState<LessonOccurrenceClickPayload | null>(null);
  const [playEventDetailOccurrence, setPlayEventDetailOccurrence] = useState<ScheduleOccurrence | null>(null);
  const [pendingRunningScheduleEditLesson, setPendingRunningScheduleEditLesson] = useState<Lesson | null>(null);
  const { activeAgeGroups: academyAgeGroups, ageGroupLabelById, ageGroupById } = useAgeGroupLookups(currentAcademy?.id);
  const { programs: cachedPrograms } = usePrograms(currentAcademy?.id, {
    includeInactive: true,
    enabled: !!currentAcademy?.id && hasProgramsAccess,
  });
  const refreshLessonsForCurrentFilter = useCallback(async () => {
    await fetchLessonsForFilter(undefined, { force: true });
  }, [fetchLessonsForFilter]);

  const programs = hasProgramsAccess ? cachedPrograms : [];
  const programNameById = useMemo(
    () => Object.fromEntries(programs.map((program) => [program.id, program.name])),
    [programs]
  );
  const calendarTooltipContext = useMemo(
    () => ({ ageGroupLabelById, programNameById }),
    [ageGroupLabelById, programNameById]
  );

  useEffect(() => {
    if (!currentAcademy?.id || (!showForm && !editingLesson)) return;
    void ensureCoachesForLessonUi();
  }, [currentAcademy?.id, showForm, editingLesson, ensureCoachesForLessonUi]);

  useEffect(() => {
    if (!sessionDetailOccurrence) return;
    void ensureCoachesForLessonUi();
  }, [sessionDetailOccurrence, ensureCoachesForLessonUi]);

  const fetchOverridesForRange = useCallback(
    async (start: Date, end: Date) => {
      if (!currentAcademy?.id) return;
      await fetchOwnerScheduleForRange(currentAcademy.id, start, end);
    },
    [currentAcademy?.id, fetchOwnerScheduleForRange]
  );

  const handleCalendarMonthNavigate = useCallback(
    (year: number, month: number) => {
      void ensureCoachesForLessonUi();
      const start = new Date(year, month, 1);
      const end = new Date(year, month + 1, 0);
      void fetchOverridesForRange(start, end);
    },
    [ensureCoachesForLessonUi, fetchOverridesForRange]
  );

  const handleCalendarWeekNavigate = useCallback(
    (weekStart: Date) => {
      void ensureCoachesForLessonUi();
      const start = new Date(weekStart);
      start.setDate(start.getDate() - 1);
      const end = new Date(weekStart);
      end.setDate(end.getDate() + 8);
      void fetchOverridesForRange(start, end);
    },
    [ensureCoachesForLessonUi, fetchOverridesForRange]
  );

  // Fetch calendar data for academy schedule view (current month -1 to current month +3)
  useEffect(() => {
    if (!currentAcademy?.id) return;
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 3, 0);
    void fetchOverridesForRange(start, end);
  }, [currentAcademy?.id, fetchOverridesForRange]);

  useEffect(() => {
    const t = setTimeout(() => setStudentSearchDebounced(studentSearchQuery), 300);
    return () => clearTimeout(t);
  }, [studentSearchQuery]);

  useEffect(() => {
    const t = setTimeout(() => setCoachSearchDebounced(coachSearchQuery), 300);
    return () => clearTimeout(t);
  }, [coachSearchQuery]);

  useEffect(() => {
    if (!currentAcademy?.id) return undefined;
    const q = studentSearchDebounced.trim();
    if (q.length < 1) return undefined;
    let cancelled = false;
    void Promise.resolve().then(() => {
      if (cancelled) return;
      setStudentSearchLoading(true);
      academyStudentService
        .getAcademyStudentsPaginated(currentAcademy.id, 1, 25, {
          searchQuery: q,
          isActive: true,
        })
        .then((res) => {
          if (!cancelled) setStudentPickerStudents(res.data ?? []);
        })
        .catch(() => {
          if (!cancelled) setStudentPickerStudents([]);
        })
        .finally(() => {
          if (!cancelled) setStudentSearchLoading(false);
        });
    });
    return () => {
      cancelled = true;
    };
  }, [currentAcademy?.id, studentSearchDebounced]);

  useEffect(() => {
    if (!currentAcademy?.id) return undefined;
    const q = coachSearchDebounced.trim();
    if (q.length < 1) return undefined;
    let cancelled = false;
    void Promise.resolve().then(() => {
      if (cancelled) return;
      setCoachSearchLoading(true);
      coachService
        .getCoaches(currentAcademy.id, { search: q })
        .then((res) => {
          if (!cancelled) setCoachPickerCoaches(res.data ?? []);
        })
        .catch(() => {
          if (!cancelled) setCoachPickerCoaches([]);
        })
        .finally(() => {
          if (!cancelled) setCoachSearchLoading(false);
        });
    });
    return () => {
      cancelled = true;
    };
  }, [currentAcademy?.id, coachSearchDebounced]);

  const toggleCoachFilterRow = useCallback((c: Coach) => {
    setCoachFilterSelection((s) => {
      if (s.ids.includes(c.id)) {
        const meta = { ...s.meta };
        delete meta[c.id];
        return { ids: s.ids.filter((x) => x !== c.id), meta };
      }
      return { ids: [...s.ids, c.id], meta: { ...s.meta, [c.id]: c } };
    });
  }, []);

  const toggleStudentFilterRow = useCallback((p: StudentProfile) => {
    setStudentFilterSelection((s) => {
      if (s.ids.includes(p.id)) {
        const meta = { ...s.meta };
        delete meta[p.id];
        return { ids: s.ids.filter((x) => x !== p.id), meta };
      }
      return { ids: [...s.ids, p.id], meta: { ...s.meta, [p.id]: p } };
    });
  }, []);

  useEffect(() => {
    const academyId = currentAcademy?.id;
    if (!academyId || studentFilterSelection.ids.length === 0) {
      setStudentEnrolledLessonIds(null);
      return undefined;
    }
    let cancelled = false;
    const ids = [...studentFilterSelection.ids];
    void Promise.resolve().then(async () => {
      try {
        const perStudent = await Promise.all(
          ids.map((studentId) =>
            academyStudentService.getStudentEnrolledLessonIds(academyId, studentId)
          )
        );
        if (cancelled) return;
        const union = new Set<string>();
        for (const arr of perStudent) {
          arr.forEach((lessonId) => union.add(lessonId));
        }
        setStudentEnrolledLessonIds(union);
      } catch {
        if (!cancelled) setStudentEnrolledLessonIds(new Set());
      }
    });
    return () => {
      cancelled = true;
    };
  }, [currentAcademy?.id, studentFilterSelection.ids.join(',')]);

  const fetchListPage = React.useCallback(async (page: number) => {
    if (!currentAcademy?.id || viewMode !== 'list') return;
    setListLoading(true);
    try {
      const filters: Parameters<typeof lessonService.getLessonsPaginated>[3] = {};
      if (selectedTermFilter !== 'all') filters.termId = selectedTermFilter;
      if (coachFilterSelection.ids.length > 0) {
        filters.coachIds = coachFilterSelection.ids.join(',');
      }
      if (selectedLessonTypeFilter !== 'all') {
        filters.lessonType = selectedLessonTypeFilter as 'group' | 'private' | 'camp';
      }
      if (selectedSkillLevelFilter !== 'all') filters.skillLevel = selectedSkillLevelFilter;
      if (selectedAgeGroupFilter !== 'all') filters.ageGroup = selectedAgeGroupFilter;
      if (selectedCourtFilter !== 'all') filters.courtId = selectedCourtFilter;
      Object.assign(filters, lessonFiltersForActiveStatus(selectedActiveFilter));
      if (selectedLocationFilter !== 'all') filters.locationId = selectedLocationFilter;
      if (selectedYearFilter !== 'all') filters.year = selectedYearFilter;
      filters.sortBy = sortBy;
      filters.sortOrder = sortOrder;
      const result = await lessonService.getLessonsPaginated(
        currentAcademy.id,
        page,
        LIST_PAGE_SIZE,
        filters
      );
      setPagedLessons(result.data);
      setListPagination(result.pagination);
      setListPage(page);
    } catch {
      toast.error('Failed to load lessons');
    } finally {
      setListLoading(false);
    }
  }, [
    currentAcademy?.id,
    viewMode,
    selectedTermFilter,
    coachFilterSelection.ids.join(','),
    selectedLessonTypeFilter,
    selectedSkillLevelFilter,
    selectedAgeGroupFilter,
    selectedCourtFilter,
    selectedActiveFilter,
    selectedLocationFilter,
    selectedYearFilter,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    if (viewMode === 'list') {
      fetchListPage(1);
    }
  }, [viewMode, currentAcademy?.id, fetchListPage]);

  type ActiveLessonCurrency = Extract<Currency, 'USD' | 'AUD' | 'GBP'>;
  const resolveActiveLessonCurrency = (currency: Currency): ActiveLessonCurrency =>
    currency === 'EUR' ? 'USD' : currency;

  // Get academy's currency
  const academyCurrency: ActiveLessonCurrency = resolveActiveLessonCurrency(
    currentAcademy?.currency || getCurrencyFromRegion(currentAcademy?.region)
  );
  const currencySymbol = getCurrencySymbol(academyCurrency);



  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
    setValue,
    getValues,
  } = useForm<LessonFormData>({
    resolver: zodResolver(lessonFormSchema),
    defaultValues: buildEmptyLessonFormValues(),
  });

  const watchedLessonType = watch('lessonType');
  const prevLessonTypeRef = useRef(watchedLessonType);
  /** When true, next lessonType change came from handleEdit/handleCancelEdit — do not run "clear on type change" */
  const skipLessonTypeChangeClearRef = useRef(false);

  const watchedImageUrl = watch('imageUrl') || '';

  const {
    displayUrl: coverDisplayUrl,
    uploading: uploadingCover,
    handleFileSelect: handleCoverFile,
    handleRemove: handleCoverRemove,
    clear: clearCoverUi,
    resolveImagePayloadForSubmit: resolveCoverImagePayload,
  } = useCoverImageUpload({
    academyId: currentAcademy?.id,
    entityId: editingLesson?.id,
    imageUrl: watchedImageUrl,
    setImageUrl: (url) => setValue('imageUrl', url, { shouldDirty: true }),
    upload: uploadLessonImage,
  });

  const { applyProgramTemplate, helperText: programTemplateHelperText } = useProgramLessonTemplate({
    programs,
    setValue,
    clearCoverUi,
  });

  useEffect(() => {
    if (skipLessonTypeChangeClearRef.current) {
      skipLessonTypeChangeClearRef.current = false;
      prevLessonTypeRef.current = watchedLessonType;
      return;
    }
    if (prevLessonTypeRef.current && prevLessonTypeRef.current !== watchedLessonType) {
      setCampWeekCount(1);
      reset({
        ...buildEmptyLessonFormValues(),
        lessonType: watchedLessonType,
        ...campFormDefaultsForLessonType(watchedLessonType),
        maxCapacity:
          watchedLessonType === 'private'
            ? watch('maxCapacity') && watch('maxCapacity')! >= 1
              ? watch('maxCapacity')
              : 1
            : campFormDefaultsForLessonType(watchedLessonType).maxCapacity ?? 6,
      });
    }
    prevLessonTypeRef.current = watchedLessonType;
  }, [watchedLessonType, reset]);

  // Auto-set max capacity to 1 when private lesson is selected
  useEffect(() => {
    if (watchedLessonType === 'private') {
      const cap = watch('maxCapacity');
      if (cap === undefined || cap === null || cap < 1) {
        setValue('maxCapacity', 1);
      }
    } else if (!watch('maxCapacity')) {
      setValue('maxCapacity', 6);
    }
  }, [watchedLessonType, setValue, watch]);

  const selectedTermIds = watch('termIds') || [];
  const isPrivateTermLinked =
    watchedLessonType === 'private' && selectedTermIds.length > 0;
  const selectedCourtIds = watch('courtIds') || [];
  const selectedCoachIds = watch('coachIds') || [];
  const watchedLocationId = watch('locationId') || '';
  const selectedDaysOfWeek = watch('daysOfWeek') || [1];
  const watchedStartTime = watch('startTime');
  const watchedEndTime = watch('endTime');
  const watchedPrice = watch('price') || 0;
  const watchedMaxCapacity = watch('maxCapacity') || 0;
  const watchedStartDate = watch('startDate');
  const watchedPrivateSessionDates = watch('privateSessionDates') || [];
  const watchedEndDate = watch('endDate');
  const watchedNumberOfSessions = watch('numberOfSessions') || 0;
  const watchedProgramId = watch('programId');
  const watchedAcademySportId = watch('academySportId');
  const watchedCampName = watch('name') || '';

  const computedCampWeekDates = useCampWeekSchedule(
    showForm && watchedLessonType === 'camp',
    watchedStartDate || '',
    selectedDaysOfWeek,
    setValue
  );

  const courtHireScheduleRange = useMemo(() => {
    if (watchedLessonType === 'camp') {
      return null;
    }
    if (isPrivateTermLinked || watchedLessonType === 'group') {
      const selectedTerms = terms.filter((term) => selectedTermIds.includes(term.id));
      if (selectedTerms.length === 0) {
        return null;
      }
      const startDate = selectedTerms.reduce(
        (min, term) => {
          const ymd = formatDateToFormYmd(term.startDate);
          return ymd < min ? ymd : min;
        },
        formatDateToFormYmd(selectedTerms[0]!.startDate)
      );
      const endDate = selectedTerms.reduce(
        (max, term) => {
          const ymd = formatDateToFormYmd(term.endDate);
          return ymd > max ? ymd : max;
        },
        formatDateToFormYmd(selectedTerms[0]!.endDate)
      );
      return { startDate, endDate };
    }
    if (watchedLessonType === 'private' && watchedStartDate) {
      return {
        startDate: watchedStartDate,
        endDate: watchedEndDate || watchedStartDate,
      };
    }
    return null;
  }, [
    watchedLessonType,
    isPrivateTermLinked,
    selectedTermIds.join(','),
    terms,
    watchedStartDate,
    watchedEndDate,
  ]);

  const clubTimeZone = currentClub?.timezone || currentAcademy?.timezone || 'UTC';
  const { bookings: courtHireBookings } = useCourtHireBookingsInRange({
    clubId: currentClub?.id,
    from: courtHireScheduleRange?.startDate,
    to: courtHireScheduleRange?.endDate,
    enabled: showForm && sourceAccess.canShowCourtHire && Boolean(courtHireScheduleRange),
  });

  const syncPrivateSessionDatesToForm = useCallback(
    (dates: string[]) => {
      const sorted = sortPrivateSessionDates(dates);
      setValue('privateSessionDates', sorted, { shouldValidate: true, shouldDirty: true });
      if (sorted.length > 0) {
        setValue('startDate', sorted[0]!, { shouldValidate: true });
        setValue('numberOfSessions', sorted.length, { shouldValidate: true });
        setValue('daysOfWeek', weekdaysFromPrivateSessionDates(sorted), { shouldValidate: true });
      } else {
        setValue('startDate', '', { shouldValidate: true });
        setValue('numberOfSessions', undefined, { shouldValidate: true });
      }
    },
    [setValue]
  );

  useEffect(() => {
    if (!showForm || watchedLessonType !== 'private' || isPrivateTermLinked) return;
    const sorted = sortPrivateSessionDates(watchedPrivateSessionDates);
    const currentStart = watch('startDate');
    const currentCount = watch('numberOfSessions');
    if (sorted.length > 0 && (currentStart !== sorted[0] || currentCount !== sorted.length)) {
      setValue('startDate', sorted[0]!, { shouldValidate: false });
      setValue('numberOfSessions', sorted.length, { shouldValidate: false });
      setValue('daysOfWeek', weekdaysFromPrivateSessionDates(sorted), { shouldValidate: false });
    }
  }, [
    watchedPrivateSessionDates.join(','),
    watchedLessonType,
    isPrivateTermLinked,
    showForm,
    setValue,
    watch,
  ]);

  useEffect(() => {
    if (!showForm) return;
    const allowed = new Set(termsForLessonForm.map((t) => t.id));
    const current = getValues('termIds') ?? [];
    const next = current.filter((id) => allowed.has(id));
    if (next.length !== current.length) {
      setValue('termIds', next, { shouldValidate: true, shouldDirty: true });
    }
  }, [showForm, termsForLessonForm, getValues, setValue]);

  // Helper functions for multi-select
  const toggleTermSelection = (termId: string) => {
    const current = selectedTermIds;
    const updated = current.includes(termId)
      ? current.filter(id => id !== termId)
      : [...current, termId];
    setValue('termIds', updated);
  };

  const activeCourtsForPicker = useMemo(() => {
    let active = courts.filter((c) => c.isActive);
    if (hasMultiLocation && locations.length > 0 && watchedLocationId) {
      active = active.filter((c) => c.locationId === watchedLocationId);
    }
    const sportId = watchedAcademySportId || defaultAcademySportId;
    if (sportId) {
      active = active.filter((c) => {
        const ids = c.academySportIds ?? [];
        return ids.length === 0 || ids.includes(sportId);
      });
    }
    return active;
  }, [
    courts,
    hasMultiLocation,
    locations.length,
    watchedLocationId,
    watchedAcademySportId,
    defaultAcademySportId,
  ]);

  const programsForLessonForm = useMemo(() => {
    const sportId = watchedAcademySportId || defaultAcademySportId;
    const selectedId = watchedProgramId;
    if (!sportId) return programs;
    return programs.filter(
      (p) => !p.academySportId || p.academySportId === sportId || p.id === selectedId
    );
  }, [programs, watchedAcademySportId, defaultAcademySportId, watchedProgramId]);

  useEffect(() => {
    if (!showForm || !hasMultiLocation || locations.length === 0) return;
    const ids = getValues('courtIds') || [];
    const allowed = new Set(activeCourtsForPicker.map((c) => c.id));
    const next = ids.filter((id) => allowed.has(id));
    if (next.length !== ids.length) {
      setValue('courtIds', next, { shouldValidate: true });
    }
  }, [watchedLocationId, showForm, hasMultiLocation, locations.length, activeCourtsForPicker, setValue, getValues]);

  const toggleCourtSelection = (courtId: string) => {
    const current = selectedCourtIds;
    const updated = current.includes(courtId)
      ? current.filter(id => id !== courtId)
      : [...current, courtId];
    setValue('courtIds', updated);
  };

  const toggleCoachSelection = (coachId: string) => {
    const current = selectedCoachIds;
    const updated = current.includes(coachId)
      ? current.filter(id => id !== coachId)
      : [...current, coachId];
    setValue('coachIds', updated);
  };

  const toggleDaySelection = (dayIndex: number) => {
    const current = selectedDaysOfWeek;
    const updated = current.includes(dayIndex)
      ? current.filter(d => d !== dayIndex)
      : [...current, dayIndex].sort();
    setValue('daysOfWeek', updated);
  };

  // Helper function to generate shareable link for private lessons
  const getShareableLink = (lessonId: string) => {
    if (!currentAcademy?.slug) return '';
    const baseUrl = window.location.origin;
    return `${baseUrl}/academy/${currentAcademy.slug}/book/${lessonId}`;
  };

  // Helper function to copy link to clipboard
  const copyShareableLink = async (lessonId: string, lessonName: string) => {
    const link = getShareableLink(lessonId);
    try {
      await navigator.clipboard.writeText(link);
      toast.success(`Link copied for "${lessonName}"!`);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  // Postpone individual lesson date
  const handleOpenPostpone = async (lesson: Lesson) => {
    if (!lesson.termId || lesson.cancelledFromDate || !currentAcademy?.id) return;
    const term = terms.find((t) => t.id === lesson.termId);
    if (!term) {
      toast.error('Term not found for this lesson');
      return;
    }
    setPostponeLoading(true);
    try {
      const overrides = await lessonOverrideService.getOverridesForLesson(lesson.id, currentAcademy.id);
      const lessonOverrides = overrides.map((o) => ({
        id: o.id,
        lessonId: o.lessonId,
        academyId: lesson.academyId,
        originalDate: new Date(o.originalDate),
        overrideType: o.overrideType,
        reason: o.reason,
        newDate: o.newDate ? new Date(o.newDate) : undefined,
        newStartTime: o.newStartTime,
        newEndTime: o.newEndTime,
        newCourtId: o.newCourtId,
        createdBy: '',
        createdAt: o.createdAt ? new Date(o.createdAt) : new Date(),
        updatedAt: o.createdAt ? new Date(o.createdAt) : new Date(),
      }));
      const occurrences = generateLessonOccurrences(
        {
          id: lesson.id,
          daysOfWeek: lesson.daysOfWeek ?? [0],
          startTime: lesson.startTime,
          endTime: lesson.endTime,
        },
        { startDate: new Date(term.startDate), endDate: new Date(term.endDate) },
        lessonOverrides
      );
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const upcoming = occurrences
        .filter((occ) => occ.date >= now && occ.status === 'scheduled')
        .slice(0, 20)
        .map((occ) => {
          const d = occ.date;
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return {
            date: `${y}-${m}-${day}`,
            label: d.toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            }),
          };
        });
      if (upcoming.length === 0) {
        toast.error('No upcoming dates available to postpone');
        return;
      }
      setLessonForPostpone(lesson);
      setPostponeOccurrenceDates(upcoming);
      setPostponeOriginalDate(null);
      setShowPostponeModal(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load lesson dates');
    } finally {
      setPostponeLoading(false);
    }
  };

  const handlePostponeConfirm = async (
    originalDate: string,
    newDate: string,
    newStartTime: string,
    newEndTime: string,
    reason: string,
    notifyStudents: boolean,
    notifySms: boolean
  ) => {
    if (!lessonForPostpone || !currentAcademy?.id) return;
    await lessonOverrideService.postponeLesson(
      lessonForPostpone.id,
      originalDate,
      newDate,
      newStartTime,
      newEndTime,
      reason,
      notifyStudents,
      undefined,
      currentAcademy.id,
      notifySms
    );
    const notified =
      notifyStudents || (smsAvailable && notifySms);
    toast.success(
      notified
        ? 'Lesson postponed. Students/guardians have been notified.'
        : 'Lesson postponed.'
    );
    setShowPostponeModal(false);
    setLessonForPostpone(null);
    setPostponeOccurrenceDates([]);
    setPostponeOriginalDate(null);
    await refreshCalendarAfterOverride();
  };

  const refreshCalendarAfterOverride = useCallback(async () => {
    await refreshLessonsForCurrentFilter();
    await facilityLayer.refresh();
    if (!currentAcademy?.id) return;
    await refreshOwnerScheduleAfterMutation(currentAcademy.id);
  }, [
    currentAcademy?.id,
    facilityLayer.refresh,
    refreshLessonsForCurrentFilter,
    refreshOwnerScheduleAfterMutation,
  ]);

  const handleLessonOccurrenceClick = useCallback((payload: LessonOccurrenceClickPayload) => {
    if (showForm) return;
    setPlayEventDetailOccurrence(null);
    setSessionDetailOccurrence(payload);
  }, [showForm]);

  const handleScheduleOccurrenceClick = useCallback((occurrence: ScheduleOccurrence) => {
    if (showForm) return;
    if (occurrence.sourceType === 'play_event') {
      setSessionDetailOccurrence(null);
      setPlayEventDetailOccurrence(occurrence);
    }
  }, [showForm]);


  const openCreateLessonForm = useCallback((prefillDate?: Date) => {
    setSessionDetailOccurrence(null);
    setEditingLesson(null);
    setPostCreateLesson(null);
    setPostCreateDrawerFooter(null);
    clearCoverUi();
    skipLessonTypeChangeClearRef.current = true;
    setConflicts([]);
    setAvailabilityWarnings([]);
    lastProcessedRef.current = '';
    setFormDetailLevel('quick');
    setCampWeekCount(1);
    reset({
      ...buildQuickCreateLessonFormValues(prefillDate),
      academySportId: defaultAcademySportId,
    });
    setShowForm(true);
  }, [clearCoverUi, reset, defaultAcademySportId]);

  const createFromUrlHandledRef = useRef(false);
  useEffect(() => {
    if (createFromUrlHandledRef.current) return;
    if (searchParams.get('create') !== 'private') return;
    if (loading || showForm) return;
    createFromUrlHandledRef.current = true;
    openCreateLessonForm();
    const next = new URLSearchParams(searchParams);
    next.delete('create');
    setSearchParams(next, { replace: true });
  }, [loading, openCreateLessonForm, searchParams, setSearchParams, showForm]);

  const duplicateLesson = useCallback(
    (lesson: Lesson, prefillDate?: Date) => {
      if (!canQuickDuplicateLesson(lesson)) {
        toast.error(
          'Only standalone lessons can be duplicated here. Use Terms or Camps for program lessons.'
        );
        return;
      }
      setSessionDetailOccurrence(null);
      setEditingLesson(null);
      setPostCreateLesson(null);
      setPostCreateDrawerFooter(null);
      clearCoverUi();
      skipLessonTypeChangeClearRef.current = true;
      setConflicts([]);
      setAvailabilityWarnings([]);
      lastProcessedRef.current = '';
      setFormDetailLevel('quick');
      setCampWeekCount(1);
      reset(buildDuplicateLessonFormValues(lesson, prefillDate));
      setShowForm(true);
    },
    [clearCoverUi, reset]
  );

  // Ensure academy context without force-refreshing on every calendar visit (avoids render churn).
  useEffect(() => {
    if (!user || !hasAcademyManagementRole(user)) {
      navigate('/dashboard');
      return;
    }

    void (async () => {
      try {
        let academies = getUserAcademiesFromCache();
        if (academies.length === 0) {
          await loadUserAcademies();
          academies = getUserAcademiesFromCache();
        }
        if (!currentAcademy?.id && academies.length > 0) {
          await loadCurrentAcademy(academies[0].id);
        }
      } catch (error) {
        console.error('Error ensuring academy context:', error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  // Academy data may still be loading — render a spinner below instead of redirecting

  // Refresh academy data when page gains focus (e.g., returning from Settings)
  useEffect(() => {
    const handleFocus = async () => {
      if (currentAcademy?.id) {
        try {
          // Force refresh to get latest Stripe Connect status
          await loadCurrentAcademy(currentAcademy.id, { force: true });
        } catch (error) {
          console.error('Error refreshing academy on focus:', error);
        }
      }
    };

    const handleVisibilityChange = async () => {
      // Also refresh when page becomes visible (handles tab switching)
      if (document.visibilityState === 'visible' && currentAcademy?.id) {
        try {
          await loadCurrentAcademy(currentAcademy.id, { force: true });
        } catch (error) {
          console.error('Error refreshing academy on visibility change:', error);
        }
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentAcademy?.id, loadCurrentAcademy]);

  // Check for conflicts when form values change
  // Use a ref to track the last processed state to prevent infinite loops
  const lastProcessedRef = useRef<string>('');

  useEffect(() => {
    if (!showForm || !selectedDaysOfWeek || !watchedStartTime || !watchedEndTime) {
      setConflicts((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    if (watchedLessonType === 'group' && selectedTermIds.length === 0) {
      setConflicts((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    if (isPrivateTermLinked && selectedTermIds.length === 0) {
      setConflicts((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    if (watchedLessonType === 'camp') {
      setConflicts((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    if (selectedCourtIds.length === 0 || selectedCoachIds.length === 0) {
      setConflicts((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    // Create a stable key to track if we've already processed this combination
    const stateKey = JSON.stringify({
      lessonType: watchedLessonType,
      termIds: selectedTermIds.sort(),
      courtIds: selectedCourtIds.sort(),
      coachIds: selectedCoachIds.sort(),
      daysOfWeek: selectedDaysOfWeek,
      startTime: watchedStartTime,
      endTime: watchedEndTime,
      startDate: watchedStartDate,
      endDate: watchedEndDate,
      lessonsCount: lessons.length,
      editingLessonId: editingLesson?.id,
    });

    // Skip if we've already processed this exact state
    if (lastProcessedRef.current === stateKey) {
      return;
    }

    lastProcessedRef.current = stateKey;

    const detectedConflicts: Array<{
      type: 'court' | 'coach';
      lesson: Lesson;
      termName: string;
      courtName: string;
      coachName: string;
    }> = [];

    // Standalone private lessons: check conflicts across ALL lessons (not just within a term)
    if (watchedLessonType === 'private' && !isPrivateTermLinked) {
      for (const courtId of selectedCourtIds) {
        const courtConflicts = lessons.filter(lesson => {
          if (lesson.id === editingLesson?.id) return false;
          const lessonDays = lesson.daysOfWeek || ((lesson as any).dayOfWeek !== undefined ? [(lesson as any).dayOfWeek] : []);
          return (
            getLessonCourtIds(lesson).includes(courtId) &&
            lessonDays.some(day => selectedDaysOfWeek.includes(day)) &&
            doTimesOverlap(lesson.startTime, lesson.endTime, watchedStartTime, watchedEndTime)
          );
        });
        courtConflicts.forEach(lesson => {
          const term = lesson.termId ? terms.find(t => t.id === lesson.termId) : null;
          const termName = term?.name || (lesson.termId ? 'Unknown Term' : lesson.lessonType === 'camp' ? 'Camp' : 'Private Lesson');
          const court = courts.find(c => c.id === courtId);
          const courtName = court?.name || 'Unknown Court';
          const coach = coaches.find(c => c.id === getLessonCoachIds(lesson)[0]);
          const coachName = coach ? `${coach.firstName} ${coach.lastName}` : 'Unknown Coach';
          if (!detectedConflicts.some(c => c.lesson.id === lesson.id && c.type === 'court')) {
            detectedConflicts.push({ type: 'court', lesson, termName, courtName, coachName });
          }
        });
      }
      for (const coachId of selectedCoachIds) {
        const coachConflicts = lessons.filter(lesson => {
          if (lesson.id === editingLesson?.id) return false;
          const lessonDays = lesson.daysOfWeek || ((lesson as any).dayOfWeek !== undefined ? [(lesson as any).dayOfWeek] : []);
          return (
            getLessonCoachIds(lesson).includes(coachId) &&
            lessonDays.some(day => selectedDaysOfWeek.includes(day)) &&
            doTimesOverlap(lesson.startTime, lesson.endTime, watchedStartTime, watchedEndTime)
          );
        });
        coachConflicts.forEach(lesson => {
          const term = lesson.termId ? terms.find(t => t.id === lesson.termId) : null;
          const termName = term?.name || (lesson.termId ? 'Unknown Term' : lesson.lessonType === 'camp' ? 'Camp' : 'Private Lesson');
          const court = courts.find(c => c.id === getLessonCourtIds(lesson)[0]);
          const courtName = court?.name || 'Unknown Court';
          const coach = coaches.find(c => c.id === coachId);
          const coachName = coach ? `${coach.firstName} ${coach.lastName}` : 'Unknown Coach';
          if (!detectedConflicts.some(c => c.lesson.id === lesson.id && c.type === 'coach')) {
            detectedConflicts.push({ type: 'coach', lesson, termName, courtName, coachName });
          }
        });
      }
    } else if (watchedLessonType === 'group' || isPrivateTermLinked) {
      // Group and term-bound private: check conflicts within the same program/term
      // Supports multi-court and multi-coach
      for (const termId of selectedTermIds) {
        for (const courtId of selectedCourtIds) {
          const courtConflicts = lessons.filter(lesson => {
            if (lesson.id === editingLesson?.id) return false;
            const lessonDays = lesson.daysOfWeek || ((lesson as any).dayOfWeek !== undefined ? [(lesson as any).dayOfWeek] : []);
            return (
              getLessonCourtIds(lesson).includes(courtId) &&
              lesson.termId === termId &&
              lessonDays.some(day => selectedDaysOfWeek.includes(day)) &&
              doTimesOverlap(lesson.startTime, lesson.endTime, watchedStartTime, watchedEndTime)
            );
          });
          courtConflicts.forEach(lesson => {
            const term = terms.find(t => t.id === lesson.termId);
            const termName = term?.name || 'Unknown Term';
            const court = courts.find(c => c.id === courtId);
            const courtName = court?.name || 'Unknown Court';
            const coach = coaches.find(c => c.id === getLessonCoachIds(lesson)[0]);
            const coachName = coach ? `${coach.firstName} ${coach.lastName}` : 'Unknown Coach';
            if (!detectedConflicts.some(c => c.lesson.id === lesson.id && c.type === 'court')) {
              detectedConflicts.push({ type: 'court', lesson, termName, courtName, coachName });
            }
          });
        }
        for (const coachId of selectedCoachIds) {
          const coachConflicts = lessons.filter(lesson => {
            if (lesson.id === editingLesson?.id) return false;
            const lessonDays = lesson.daysOfWeek || ((lesson as any).dayOfWeek !== undefined ? [(lesson as any).dayOfWeek] : []);
            return (
              getLessonCoachIds(lesson).includes(coachId) &&
              lesson.termId === termId &&
              lessonDays.some(day => selectedDaysOfWeek.includes(day)) &&
              doTimesOverlap(lesson.startTime, lesson.endTime, watchedStartTime, watchedEndTime)
            );
          });
          coachConflicts.forEach(lesson => {
            const term = terms.find(t => t.id === lesson.termId);
            const termName = term?.name || 'Unknown Term';
            const court = courts.find(c => c.id === getLessonCourtIds(lesson)[0]);
            const courtName = court?.name || 'Unknown Court';
            const coach = coaches.find(c => c.id === coachId);
            const coachName = coach ? `${coach.firstName} ${coach.lastName}` : 'Unknown Coach';
            if (!detectedConflicts.some(c => c.lesson.id === lesson.id && c.type === 'coach')) {
              detectedConflicts.push({ type: 'coach', lesson, termName, courtName, coachName });
            }
          });
        }
      }
    }

    setConflicts(detectedConflicts);
  }, [
    selectedDaysOfWeek.join(','),
    watchedStartTime,
    watchedEndTime,
    lessons.length,
    courts.length,
    coaches.length,
    terms.length,
    showForm,
    editingLesson?.id,
    watchedLessonType,
    isPrivateTermLinked,
    selectedTermIds.join(','),
    watchedStartDate,
    watchedEndDate,
  ]);

  const schedulingConflictSummary = useMemo(() => {
    const active = conflicts.filter((c) => c.lesson.isActive !== false);
    const inactive = conflicts.filter((c) => c.lesson.isActive === false);
    return { active, inactive, hasActive: active.length > 0, hasInactive: inactive.length > 0 };
  }, [conflicts]);

  const coachesAvailabilityKey = useMemo(
    () =>
      coaches
        .map((c) => {
          const extended = c as Coach & {
            availability?: Array<{ dayOfWeek: number; startTime: string; endTime: string; isAvailable: boolean }>;
          };
          const availability =
            extended.availability
              ?.map((slot) => `${slot.dayOfWeek}:${slot.startTime}:${slot.endTime}:${slot.isAvailable}`)
              .join(',') ?? '';
          return `${c.id}|${c.firstName}|${c.lastName}|${availability}`;
        })
        .join(';'),
    [coaches]
  );

  // Check for coach availability warnings
  useEffect(() => {
    if (!showForm || !selectedCoachIds || selectedCoachIds.length === 0 || !selectedDaysOfWeek || !watchedStartTime || !watchedEndTime) {
      setAvailabilityWarnings((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    const warnings = selectedCoachIds
      .map((coachId) => {
        const coach = coaches.find((c) => c.id === coachId) as (typeof coaches[number] & {
          availability?: Array<{ dayOfWeek: number; startTime: string; endTime: string; isAvailable: boolean }>;
        }) | undefined;
        if (!coach) return null;

        const missingDays = getCoachAvailabilityMissingDays(
          coach,
          selectedDaysOfWeek,
          watchedStartTime,
          watchedEndTime
        );

        if (missingDays.length === 0) return null;
        return {
          coachId,
          coachName: `${coach.firstName} ${coach.lastName}`,
          missingDays,
        };
      })
      .filter((warning): warning is { coachId: string; coachName: string; missingDays: number[] } => Boolean(warning));

    setAvailabilityWarnings((prev) => {
      if (prev.length === warnings.length && prev.every((item, index) => {
        const next = warnings[index];
        return (
          next &&
          item.coachId === next.coachId &&
          item.coachName === next.coachName &&
          item.missingDays.join(',') === next.missingDays.join(',')
        );
      })) {
        return prev;
      }
      return warnings;
    });
  }, [
    selectedCoachIds.join(','),
    selectedDaysOfWeek.join(','),
    watchedStartTime,
    watchedEndTime,
    showForm,
    coachesAvailabilityKey,
  ]);

  useEffect(() => {
    if (
      !showForm ||
      !selectedDaysOfWeek ||
      !watchedStartTime ||
      !watchedEndTime ||
      selectedCourtIds.length === 0 ||
      courtHireBookings.length === 0
    ) {
      setCourtHireWarnings((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    const conflictingBookings = findCourtHireBookingsConflictingWithLessonSchedule(
      courtHireBookings,
      {
        courtIds: selectedCourtIds,
        daysOfWeek: selectedDaysOfWeek,
        startTime: watchedStartTime,
        endTime: watchedEndTime,
        ...(courtHireScheduleRange
          ? { dateRange: courtHireScheduleRange }
          : {}),
        timeZone: clubTimeZone,
      }
    );

    const warnings = conflictingBookings.map((booking) => {
      const courtName = courts.find((court) => court.id === booking.courtId)?.name || 'Unknown Court';
      const startAt = new Date(booking.startAt);
      return {
        courtName,
        bookingDate: formatInTimeZone(startAt, clubTimeZone, 'd MMM yyyy'),
        bookingTime: formatInTimeZone(startAt, clubTimeZone, 'HH:mm'),
      };
    });

    setCourtHireWarnings((prev) => {
      if (
        prev.length === warnings.length &&
        prev.every(
          (item, index) =>
            item.courtName === warnings[index]?.courtName &&
            item.bookingDate === warnings[index]?.bookingDate &&
            item.bookingTime === warnings[index]?.bookingTime
        )
      ) {
        return prev;
      }
      return warnings;
    });
  }, [
    showForm,
    selectedDaysOfWeek.join(','),
    watchedStartTime,
    watchedEndTime,
    selectedCourtIds.join(','),
    courtHireBookings,
    courtHireScheduleRange?.startDate,
    courtHireScheduleRange?.endDate,
    clubTimeZone,
    courts,
  ]);

  const onSubmit = async (data: LessonFormData) => {
    if (!currentAcademy) {
      toast.error('No academy selected');
      return;
    }

    // Starter plan users can create lessons without Stripe Connect

    try {
      setSubmitting(true);

      const locationPayload =
        hasMultiLocation && locations.length > 0
          ? { locationId: data.locationId && data.locationId.length > 0 ? data.locationId : null }
          : {};

      const programIdNormalized = normalizeLessonProgramId(data.programId);

      // Validate end time is after start time
      const startTime = new Date(`2000-01-01 ${data.startTime}`);
      const endTime = new Date(`2000-01-01 ${data.endTime}`);
      if (endTime <= startTime) {
        throw new Error('End time must be after start time');
      }

      if (editingLesson) {
        const imagePayload = resolveCoverImagePayload();
        // For editing, we can only edit one lesson at a time
        if (data.lessonType === 'camp') {
          const campSessionDates = computeCampWeekSessionDates(data.startDate ?? '', data.daysOfWeek ?? []);
          if (campSessionDates.length === 0) {
            throw new Error('Select a start date and at least one day of the week');
          }
          const endDate = campSessionDates[campSessionDates.length - 1]!;
          const full = data.campFullPrice ?? data.price;
          const explicitCampSessions = campSessionDatesToExplicitSlots(
            campSessionDates,
            data.startTime!,
            data.endTime!
          );
          const lessonData: Record<string, unknown> = {
            academyId: currentAcademy.id,
            courtIds: data.courtIds!,
            coachIds: data.coachIds!,
            name: data.name!,
            description: data.description || undefined,
            lessonType: 'camp' as const,
            skillLevel: data.skillLevel || undefined,
            ageGroup: data.ageGroup || undefined,
            ageGroupId: data.ageGroupId || undefined,
            daysOfWeek: data.daysOfWeek!,
            startTime: data.startTime!,
            endTime: data.endTime!,
            maxCapacity: data.maxCapacity!,
            price: full,
            campDayPrice: data.campDayPrice!,
            campFullPrice: data.campFullPrice!,
            currency: academyCurrency,
            isActive: data.isActive!,
            isFeatured: data.isFeatured || false,
            startDate: data.startDate,
            endDate,
            campSessionDates,
            explicitCampSessions,
            programId: programIdNormalized,
            academySportId: data.academySportId || undefined,
            ...locationPayload,
            ...imagePayload,
          };
          await updateLesson(editingLesson.id, lessonData);
          toast.success('Camp updated successfully');
        } else if (data.lessonType === 'private') {
          const privateTermId =
            data.termIds && data.termIds.length === 1 ? data.termIds[0] : null;
          if (privateTermId) {
            const lessonData: Record<string, unknown> = {
              academyId: currentAcademy.id,
              termId: privateTermId,
              courtIds: data.courtIds!,
              coachIds: data.coachIds!,
              name: data.name!,
              description: data.description || undefined,
              lessonType: 'private' as const,
              skillLevel: data.skillLevel || undefined,
              ageGroup: data.ageGroup || undefined,
              ageGroupId: data.ageGroupId || undefined,
              daysOfWeek: data.daysOfWeek!,
              startTime: data.startTime!,
              endTime: data.endTime!,
              maxCapacity: data.maxCapacity ?? 1,
              price: data.price!,
              sessionPrice:
                data.sessionPrice != null &&
                !Number.isNaN(data.sessionPrice) &&
                data.sessionPrice >= 0
                  ? data.sessionPrice
                  : null,
              currency: academyCurrency,
              isActive: data.isActive!,
              isFeatured: data.isFeatured || false,
              programId: programIdNormalized,
            academySportId: data.academySportId || undefined,
              ...locationPayload,
              ...imagePayload,
            };
            await updateLesson(editingLesson.id, lessonData as any);
            toast.success('Private lesson updated successfully');
          } else {
            const sessionDates = sortPrivateSessionDates(data.privateSessionDates);
            if (sessionDates.length === 0) {
              throw new Error('Select at least one session date for this package');
            }
            const explicitPrivateSessions = privateSessionDatesToExplicitSlots(
              sessionDates,
              data.startTime!,
              data.endTime!
            );
            const lessonData: Record<string, unknown> = {
              academyId: currentAcademy.id,
              courtIds: data.courtIds!,
              coachIds: data.coachIds!,
              name: data.name!,
              description: data.description || undefined,
              lessonType: 'private' as const,
              skillLevel: data.skillLevel || undefined,
              ageGroup: data.ageGroup || undefined,
              ageGroupId: data.ageGroupId || undefined,
              daysOfWeek: weekdaysFromPrivateSessionDates(sessionDates),
              startTime: data.startTime!,
              endTime: data.endTime!,
              maxCapacity: data.maxCapacity ?? 1,
              price: data.price! * sessionDates.length,
              pricePerSession: data.price!,
              currency: academyCurrency,
              isActive: data.isActive!,
              isFeatured: data.isFeatured || false,
              startDate: sessionDates[0],
              numberOfSessions: sessionDates.length,
              termId: null,
              explicitPrivateSessions,
              programId: programIdNormalized,
            academySportId: data.academySportId || undefined,
              ...locationPayload,
              ...imagePayload,
            };
            await updateLesson(editingLesson.id, lessonData as any);
            toast.success('Private lesson updated successfully');
          }
        } else {
          // Group lesson editing
          if (!data.termIds || data.termIds.length === 0) {
            throw new Error('At least one term is required for group lessons');
          }

          const lessonData = {
            academyId: currentAcademy.id,
            termId: data.termIds[0]!,
            courtIds: data.courtIds!,
            coachIds: data.coachIds!,
            name: data.name!,
            description: data.description || undefined,
            lessonType: data.lessonType!,
            skillLevel: data.skillLevel || undefined,
            ageGroup: data.ageGroup || undefined,
            ageGroupId: data.ageGroupId || undefined,
            daysOfWeek: data.daysOfWeek!,
            startTime: data.startTime!,
            endTime: data.endTime!,
            maxCapacity: data.maxCapacity!,
            price: data.price!,
            sessionPrice:
              data.sessionPrice != null &&
              !Number.isNaN(data.sessionPrice) &&
              data.sessionPrice >= 0
                ? data.sessionPrice
                : null,
            currency: academyCurrency,
            isActive: data.isActive!,
            isFeatured: data.isFeatured || false,
            programId: programIdNormalized,
            academySportId: data.academySportId || undefined,
            ...locationPayload,
            ...imagePayload,
          };

          await updateLesson(editingLesson.id, lessonData);
          toast.success('Lesson updated successfully');
        }
      } else {
        const sessionDates = sortPrivateSessionDates(data.privateSessionDates);
        if (sessionDates.length === 0) {
          throw new Error('Select at least one session date for this lesson');
        }
        const imagePayload = resolveCoverImagePayload();
        const explicitPrivateSessions = privateSessionDatesToExplicitSlots(
          sessionDates,
          data.startTime!,
          data.endTime!
        );
        const createPayload: LessonCreateRequest = {
          academyId: currentAcademy.id,
          courtIds: data.courtIds!,
          coachIds: data.coachIds!,
          name: data.name!,
          description: data.description || undefined,
          lessonType: 'private',
          skillLevel: data.skillLevel || undefined,
          ageGroup: data.ageGroup || undefined,
          ageGroupId: data.ageGroupId || undefined,
          daysOfWeek: weekdaysFromPrivateSessionDates(sessionDates),
          startTime: data.startTime!,
          endTime: data.endTime!,
          maxCapacity: data.maxCapacity ?? 1,
          price: data.price! * sessionDates.length,
          currency: academyCurrency,
          isActive: data.isActive!,
          isFeatured: data.isFeatured || false,
          startDate: sessionDates[0],
          numberOfSessions: sessionDates.length,
          privateSessionDates: sessionDates,
          explicitPrivateSessions,
          programId: programIdNormalized,
          academySportId: data.academySportId || undefined,
          ...locationPayload,
          ...imagePayload,
        };
        const createdPrivate = await createLesson(createPayload);
        if (createdPrivate) {
          toast.success('Lesson created successfully');
          setPostCreateLesson({
            id: createdPrivate.id,
            name: createdPrivate.name,
            maxCapacity: createdPrivate.maxCapacity,
          });
          setPostCreateDrawerFooter(null);
          skipLessonTypeChangeClearRef.current = true;
          reset(buildQuickCreateLessonFormValues());
          clearCoverUi();
          await refreshCalendarAfterOverride();
          return;
        }
      }

      handleCancelEdit();
      await refreshCalendarAfterOverride();
    } catch (error: unknown) {
      console.error('Error saving lesson:', error);
      toast.error(formatSchedulingErrorMessage(error, 'Failed to save lesson'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (lesson: Lesson) => {
    setCampWeekCount(1);
    clearCoverUi();
    skipLessonTypeChangeClearRef.current = true;
    setEditingLesson(lesson);
    setFormDetailLevel('advanced');
    setShowForm(true);

    // For private lessons, calculate price per session from total price
    // Check if pricePerSession exists first, otherwise calculate from numberOfSessions
    let pricePerSession = lesson.price;
    if (lesson.lessonType === 'private') {
      if (lesson.termId) {
        pricePerSession = lesson.price;
      } else if ((lesson as any).pricePerSession) {
        pricePerSession = (lesson as any).pricePerSession;
      } else if ((lesson as any).numberOfSessions && (lesson as any).numberOfSessions > 0) {
        pricePerSession = lesson.price / (lesson as any).numberOfSessions;
      }
    } else if (lesson.lessonType === 'camp') {
      pricePerSession = lesson.campFullPrice ?? lesson.price;
    }

    let startDateString = '';
    if (lesson.lessonType === 'private' && !lesson.termId) {
      startDateString = lessonDateToFormString((lesson as any).startDate);
    } else if (lesson.lessonType === 'camp') {
      startDateString = lessonDateToFormString((lesson as any).startDate);
    }

    const numberOfSessions = lesson.termId
      ? undefined
      : (lesson as any).numberOfSessions || 10;

    let privateSessionDates: string[] = [];
    if (lesson.lessonType === 'private' && !lesson.termId) {
      try {
        const sessions = await lessonService.getLessonSessions(lesson.id);
        privateSessionDates = sortPrivateSessionDates(
          sessions.map((s) => s.sessionDate).filter((d): d is string => !!d)
        );
      } catch {
        /* fall back to lesson row */
      }
      if (privateSessionDates.length === 0 && startDateString) {
        privateSessionDates = [startDateString];
      }
    }

    const endDateString = lesson.lessonType === 'camp'
      ? lessonDateToFormString((lesson as any).endDate)
      : '';

    const rawDays =
      lesson.daysOfWeek ?? ((lesson as any).dayOfWeek !== undefined ? [(lesson as any).dayOfWeek] : [1]);
    const daysOfWeekNormalized = Array.isArray(rawDays)
      ? rawDays
          .map((d: number | string) => (typeof d === 'string' ? parseInt(d, 10) : d))
          .filter((n: number) => !Number.isNaN(n) && n >= 0 && n <= 6)
      : [1];
    const daysOfWeek = daysOfWeekNormalized.length > 0 ? daysOfWeekNormalized : [1];

    // Ensure courtIds and coachIds are arrays
    const courtIds = getLessonCourtIds(lesson);
    const coachIds = getLessonCoachIds(lesson);

    const termForEdit = lesson.termId ? terms.find((t) => t.id === lesson.termId) : null;
    const termIdsForForm =
      termForEdit && isTermSelectableForLessonForm(termForEdit) && lesson.termId ? [lesson.termId] : [];

    // Reset form with all values - set lessonType first to ensure conditional fields are shown
    reset({
      lessonType: lesson.lessonType,
      termIds: termIdsForForm,
      privateSessionDates,
      startDate: startDateString,
      endDate: endDateString,
      numberOfSessions: numberOfSessions,
      pricingType: lesson.pricingType ?? 'total',
      campDayPrice: lesson.campDayPrice ?? 45,
      campFullPrice: lesson.campFullPrice ?? lesson.price,
      campSessionDates: [],
      courtIds: courtIds,
      coachIds: coachIds,
      name: lesson.name || '',
      description: lesson.description || '',
      imageUrl: lesson.imageUrl || '',
      skillLevel: lesson.skillLevel || 'any',
      ageGroup: lesson.ageGroup || 'all',
      ageGroupId: lesson.ageGroupId || undefined,
      daysOfWeek,
      startTime: normalizeTimeForForm(lesson.startTime),
      endTime: normalizeTimeForForm(lesson.endTime),
      maxCapacity: lesson.maxCapacity,
      price: pricePerSession,
      sessionPrice: lesson.sessionPrice ?? undefined,
      isActive: lesson.isActive,
      isFeatured: lesson.isFeatured || false,
      locationId: lesson.locationId || '',
      programId: lesson.programId ?? null,
      academySportId: lesson.academySportId ?? defaultAcademySportId,
    }, {
      keepDefaultValues: false,
    });
  };

  const offerEditLesson = (lesson: Lesson) => {
    const termRow = lesson.termId ? terms.find((t) => t.id === lesson.termId) : undefined;
    if (shouldWarnWhenEditingLessonInRunningSchedule(lesson, termRow ?? null, new Date())) {
      setPendingRunningScheduleEditLesson(lesson);
      return;
    }
    handleEdit(lesson);
  };

  // Open a specific lesson when navigated here with a highlightLessonId (e.g. from the
  // low-enrollment alert or booking actions). Runs once per navigation after lessons load.
  const handledHighlightLessonRef = useRef<string | null>(null);
  useEffect(() => {
    const highlightLessonId = (location.state as { highlightLessonId?: string } | null)
      ?.highlightLessonId;
    if (!highlightLessonId || lessonsLoading) return;
    if (handledHighlightLessonRef.current === highlightLessonId) return;

    const lesson = allLessons.find((l) => l.id === highlightLessonId);
    if (!lesson) return;

    handledHighlightLessonRef.current = highlightLessonId;
    offerEditLesson(lesson);
    // Clear router state so the drawer doesn't re-open on back/refresh
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, lessonsLoading, allLessons]);

  // Effect to ensure all fields are populated after form reset when editing
  useEffect(() => {
    if (!editingLesson || !showForm) return undefined;

    let timer: ReturnType<typeof setTimeout>;

    // Use requestAnimationFrame to ensure DOM is ready, then set values
    const rafId = requestAnimationFrame(() => {
      // Then use setTimeout to ensure React has processed the form reset
      timer = setTimeout(() => {
        // Set court IDs (ensure it's an array)
        const courtIds = getLessonCourtIds(editingLesson);
        if (courtIds.length > 0) {
          setValue('courtIds', courtIds, { shouldValidate: false, shouldDirty: true });
        }

        // Set coach IDs (ensure it's an array)
        const coachIds = getLessonCoachIds(editingLesson);
        if (coachIds.length > 0) {
          setValue('coachIds', coachIds, { shouldValidate: false, shouldDirty: true });
        }

        // Set lesson name
        if (editingLesson.name) {
          setValue('name', editingLesson.name, { shouldValidate: false, shouldDirty: true });
        }

        // Set start and end times (normalize HH:MM:SS from API — schema expects HH:MM)
        if (editingLesson.startTime) {
          setValue('startTime', normalizeTimeForForm(editingLesson.startTime), {
            shouldValidate: false,
            shouldDirty: true,
          });
        }
        if (editingLesson.endTime) {
          setValue('endTime', normalizeTimeForForm(editingLesson.endTime), {
            shouldValidate: false,
            shouldDirty: true,
          });
        }

        // Set day of week
        if (editingLesson.daysOfWeek) {
          setValue('daysOfWeek', editingLesson.daysOfWeek, { shouldValidate: false, shouldDirty: true });
        } else if ((editingLesson as any).dayOfWeek !== undefined) {
          setValue('daysOfWeek', [(editingLesson as any).dayOfWeek], { shouldValidate: false, shouldDirty: true });
        }

        // For private lessons, set startDate and numberOfSessions
        if (editingLesson.lessonType === 'private') {
          const startDateString = lessonDateToFormString((editingLesson as any).startDate);
          const numberOfSessions = (editingLesson as any).numberOfSessions || 10;

          if (startDateString) {
            setValue('startDate', startDateString, { shouldValidate: false, shouldDirty: true });
          }
          if (numberOfSessions) {
            setValue('numberOfSessions', numberOfSessions, { shouldValidate: false, shouldDirty: true });
          }
        }

        if (editingLesson.lessonType === 'camp') {
          const campStartDate = lessonDateToFormString((editingLesson as any).startDate);
          if (campStartDate) {
            setValue('startDate', campStartDate, { shouldValidate: false, shouldDirty: true });
          }
          setValue('campDayPrice', editingLesson.campDayPrice ?? 45, {
            shouldValidate: false,
            shouldDirty: true,
          });
          setValue('campFullPrice', editingLesson.campFullPrice ?? editingLesson.price, {
            shouldValidate: false,
            shouldDirty: true,
          });
          setValue('price', editingLesson.campFullPrice ?? editingLesson.price, {
            shouldValidate: false,
            shouldDirty: true,
          });
        }
      }, 200);
    });

    return () => {
      cancelAnimationFrame(rafId);
      if (timer) clearTimeout(timer);
    };
  }, [editingLesson, showForm, setValue, terms]);

  const handleDelete = async (lesson: Lesson) => {
    if (!confirm(`Are you sure you want to delete "${lesson.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteLesson(lesson.id);
      await refreshCalendarAfterOverride();
      toast.success('Lesson deleted successfully');
    } catch (error: any) {
      console.error('Error deleting lesson:', error);
      toast.error(error.message || 'Failed to delete lesson');
    }
  };

  const handleCancelEdit = () => {
    clearCoverUi();
    setCampWeekCount(1);
    skipLessonTypeChangeClearRef.current = true;
    setEditingLesson(null);
    setShowForm(false);
    setPostCreateLesson(null);
    setPostCreateDrawerFooter(null);
    setFormDetailLevel('quick');
    setConflicts([]);
    setAvailabilityWarnings([]);
    lastProcessedRef.current = '';
    reset(buildEmptyLessonFormValues());
  };

  const finishPostCreate = useCallback(async () => {
    setPostCreateLesson(null);
    setPostCreateDrawerFooter(null);
    handleCancelEdit();
    await refreshCalendarAfterOverride();
  }, [refreshCalendarAfterOverride]);


  const getCourtName = (courtId: string) => {
    const court = courts.find(c => c.id === courtId);
    return court?.name || 'Unknown Court';
  };

  const getCoachById = (coachId: string) => coaches.find(c => c.id === coachId);

  const getTermName = (termId: string) => {
    const term = terms.find(t => t.id === termId);
    return term?.name || 'Unknown Term';
  };

  // Extract available years from terms and private lessons
  const availableYears = useMemo(() => {
    const years = new Set<number>();

    // Add years from terms
    terms.forEach(term => {
      if (term.startDate) {
        const year = new Date(term.startDate).getFullYear();
        years.add(year);
      }
      if (term.endDate) {
        const year = new Date(term.endDate).getFullYear();
        years.add(year);
      }
    });

    // Add years from private lessons
    lessons.forEach(lesson => {
      if (lesson.lessonType === 'private' && (lesson as any).startDate) {
        const startDate = (lesson as any).startDate;
        let date: Date | null = null;

        if (startDate instanceof Date) {
          date = startDate;
        } else if (startDate.toDate && typeof startDate.toDate === 'function') {
          date = startDate.toDate();
        } else if (startDate.seconds) {
          date = new Date(startDate.seconds * 1000);
        } else if (startDate._seconds) {
          date = new Date(startDate._seconds * 1000);
        } else if (typeof startDate === 'string') {
          date = new Date(startDate);
        }

        if (date && !isNaN(date.getTime())) {
          years.add(date.getFullYear());
        }
      }
    });

    // Years spanned by camp date ranges (standalone; no term)
    lessons.forEach((lesson) => {
      if (lesson.lessonType !== 'camp') return;
      const cs = lessonDateFieldToLocalDate(lesson.startDate);
      const ce = lessonDateFieldToLocalDate(lesson.endDate);
      if (!cs || !ce) return;
      for (let y = cs.getFullYear(); y <= ce.getFullYear(); y += 1) {
        years.add(y);
      }
    });

    return Array.from(years).sort((a, b) => b - a); // Sort descending (newest first)
  }, [terms, lessons]);

  const yearSelectOptions = useMemo(() => {
    const y = new Date().getFullYear();
    const set = new Set(availableYears);
    set.add(y);
    return Array.from(set).sort((a, b) => b - a);
  }, [availableYears]);

  // Filter terms based on selected year
  const filteredTerms = useMemo(() => {
    if (selectedYearFilter === 'all') {
      return terms;
    }

    const selectedYear = parseInt(selectedYearFilter);
    return terms.filter(term => {
      const termStartYear = term.startDate ? new Date(term.startDate).getFullYear() : null;
      const termEndYear = term.endDate ? new Date(term.endDate).getFullYear() : null;

      // Include term if the selected year falls within the term's date range
      if (termStartYear && termEndYear) {
        return selectedYear >= termStartYear && selectedYear <= termEndYear;
      } else if (termStartYear) {
        return termStartYear === selectedYear;
      } else if (termEndYear) {
        return termEndYear === selectedYear;
      }
      return false;
    });
  }, [terms, selectedYearFilter]);

  // Compute lesson/day availability warnings
  const lessonAvailabilityWarnings = useMemo(() => {
    return computeLessonAvailabilityWarningDays(lessons, coaches);
  }, [lessons, coaches]);
  const lessonAvailabilityWarningSet = useMemo(
    () => new Set(lessonAvailabilityWarnings.keys()),
    [lessonAvailabilityWarnings]
  );

  // Reset term filter if selected term is not in filtered terms when year filter changes
  useEffect(() => {
    if (selectedTermFilter !== 'all' && selectedYearFilter !== 'all') {
      const isTermInFiltered = filteredTerms.some(term => term.id === selectedTermFilter);
      if (!isTermInFiltered) {
        setSelectedTermFilter('all');
      }
    }
  }, [selectedYearFilter, filteredTerms, selectedTermFilter]);

  const termMapForStatusFilter = useMemo(
    () => new Map(terms.map((term) => [term.id, term])),
    [terms]
  );

  const scheduleActiveStatusFilter = toScheduleActiveStatusFilter(selectedActiveFilter);

  const lessonMatchesStatusFilter = useCallback(
    (lesson: Lesson) =>
      isLessonIncludedOnAcademyCalendar(
        lesson,
        termMapForStatusFilter,
        scheduleActiveStatusFilter
      ),
    [termMapForStatusFilter, scheduleActiveStatusFilter]
  );

  // Filter lessons
  const filteredLessons = lessons.filter(lesson => {
    // Filter by term
    if (selectedTermFilter !== 'all') {
      if (!lesson.termId || lesson.termId !== selectedTermFilter) {
        return false;
      }
    }

    // Filter by year
    if (selectedYearFilter !== 'all') {
      const selectedYear = parseInt(selectedYearFilter);

      if (lesson.lessonType === 'private') {
        if (lesson.termId) {
          const term = terms.find((t) => t.id === lesson.termId);
          if (term) {
            const termStartYear = term.startDate ? new Date(term.startDate).getFullYear() : null;
            const termEndYear = term.endDate ? new Date(term.endDate).getFullYear() : null;
            if (termStartYear && termEndYear) {
              if (selectedYear < termStartYear || selectedYear > termEndYear) {
                return false;
              }
            } else if (termStartYear && termStartYear !== selectedYear) {
              return false;
            } else if (termEndYear && termEndYear !== selectedYear) {
              return false;
            } else {
              return false;
            }
          } else {
            return false;
          }
        } else {
          const startDate = (lesson as any).startDate;
          if (startDate) {
            let date: Date | null = null;

            if (startDate instanceof Date) {
              date = startDate;
            } else if (startDate.toDate && typeof startDate.toDate === 'function') {
              date = startDate.toDate();
            } else if (startDate.seconds) {
              date = new Date(startDate.seconds * 1000);
            } else if (startDate._seconds) {
              date = new Date(startDate._seconds * 1000);
            } else if (typeof startDate === 'string') {
              date = new Date(startDate);
            }

            if (date && !isNaN(date.getTime())) {
              const lessonYear = date.getFullYear();
              if (lessonYear !== selectedYear) {
                return false;
              }
            } else {
              return false;
            }
          } else {
            return false;
          }
        }
      } else if (lesson.lessonType === 'camp') {
        const campStart = lessonDateFieldToLocalDate(lesson.startDate);
        const campEnd = lessonDateFieldToLocalDate(lesson.endDate);
        if (!campStart || !campEnd) {
          return false;
        }
        if (!campDateRangeOverlapsYear(campStart, campEnd, selectedYear)) {
          return false;
        }
      } else if (lesson.lessonType === 'group') {
        // Term-bound group lessons: check the term's year
        if (lesson.termId) {
          const term = terms.find(t => t.id === lesson.termId);
          if (term) {
            const termStartYear = term.startDate ? new Date(term.startDate).getFullYear() : null;
            const termEndYear = term.endDate ? new Date(term.endDate).getFullYear() : null;

            // Include if the selected year falls within the term's date range
            if (termStartYear && termEndYear) {
              if (selectedYear < termStartYear || selectedYear > termEndYear) {
                return false;
              }
            } else if (termStartYear && termStartYear !== selectedYear) {
              return false;
            } else if (termEndYear && termEndYear !== selectedYear) {
              return false;
            } else {
              return false; // No valid dates
            }
          } else {
            return false; // Term not found
          }
        } else {
          return false;
        }
      }
    }

    // Filter by lesson type
    if (selectedLessonTypeFilter !== 'all' && lesson.lessonType !== selectedLessonTypeFilter) {
      return false;
    }

    // Filter by skill level
    if (selectedSkillLevelFilter !== 'all' && lesson.skillLevel !== selectedSkillLevelFilter) {
      return false;
    }

    // Filter by age group
    if (selectedAgeGroupFilter !== 'all' && lesson.ageGroup !== selectedAgeGroupFilter) {
      return false;
    }

    // Filter by coach(s) — lesson matches if any assigned coach is selected
    if (coachFilterSelection.ids.length > 0) {
      const lessonCoachIds = getLessonCoachIds(lesson);
      if (!coachFilterSelection.ids.some((cid) => lessonCoachIds.includes(cid))) {
        return false;
      }
    }

    // Filter by court
    if (selectedCourtFilter !== 'all' && !getLessonCourtIds(lesson).includes(selectedCourtFilter)) {
      return false;
    }

    if (selectedSportFilter !== 'all') {
      const lessonSportId = lesson.academySportId ?? defaultAcademySportId;
      if (lessonSportId !== selectedSportFilter) {
        return false;
      }
    }

    // Filter by location (lesson row or any assigned court at that location)
    if (selectedLocationFilter !== 'all') {
      if (!lessonMatchesLocationFilter(lesson, selectedLocationFilter, courts)) {
        return false;
      }
    }

    if (!lessonMatchesStatusFilter(lesson)) {
      return false;
    }

    // Filter by enrolled student(s) — union of lessons any selected student is enrolled in
    if (studentFilterSelection.ids.length > 0 && studentEnrolledLessonIds && !studentEnrolledLessonIds.has(lesson.id)) {
      return false;
    }

    return true;
  });

  // Sort lessons (calendar views: name only; list view uses server-side sort on paged data)
  const sortedLessons = useMemo(() => {
    const sorted = [...filteredLessons];
    if (viewMode !== 'list') {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
      return sorted;
    }

    sorted.sort((a, b) => {
      if (sortBy === 'name') {
        const comparison = a.name.localeCompare(b.name);
        return sortOrder === 'asc' ? comparison : -comparison;
      }

      if (sortBy === 'type') {
        const typeOrder = { private: 0, group: 1, camp: 2 };
        const aOrder = typeOrder[a.lessonType] ?? 3;
        const bOrder = typeOrder[b.lessonType] ?? 3;
        if (aOrder !== bOrder) {
          return sortOrder === 'asc' ? aOrder - bOrder : bOrder - aOrder;
        }
        return a.name.localeCompare(b.name);
      }

      if (sortBy === 'price') {
        return sortOrder === 'asc' ? a.price - b.price : b.price - a.price;
      }

      if (sortBy === 'day') {
        const aDay = a.daysOfWeek?.[0] ?? (a as { dayOfWeek?: number }).dayOfWeek ?? 0;
        const bDay = b.daysOfWeek?.[0] ?? (b as { dayOfWeek?: number }).dayOfWeek ?? 0;
        if (aDay !== bDay) {
          return sortOrder === 'asc' ? aDay - bDay : bDay - aDay;
        }
        return a.startTime.localeCompare(b.startTime);
      }

      if (sortBy === 'time') {
        const timeComparison = a.startTime.localeCompare(b.startTime);
        return sortOrder === 'asc' ? timeComparison : -timeComparison;
      }

      if (sortBy === 'enrollment') {
        const aEnrollment = a.currentEnrollment || 0;
        const bEnrollment = b.currentEnrollment || 0;
        if (aEnrollment !== bEnrollment) {
          return sortOrder === 'asc' ? aEnrollment - bEnrollment : bEnrollment - aEnrollment;
        }
        return a.name.localeCompare(b.name);
      }

      if (sortBy === 'term') {
        const aTerm = a.termId ? terms.find((t) => t.id === a.termId) : null;
        const bTerm = b.termId ? terms.find((t) => t.id === b.termId) : null;
        const aName = aTerm?.name || 'No Term';
        const bName = bTerm?.name || 'No Term';
        const comparison = aName.localeCompare(bName);
        return sortOrder === 'asc' ? comparison : -comparison;
      }

      return 0;
    });

    return sorted;
  }, [filteredLessons, viewMode, sortBy, sortOrder, terms]);

  const listLessonsForStudentFilter = useMemo(() => {
    const statusFiltered = pagedLessons.filter(lessonMatchesStatusFilter);
    if (studentFilterSelection.ids.length === 0 || !studentEnrolledLessonIds) {
      return statusFiltered;
    }
    return statusFiltered.filter((l) => studentEnrolledLessonIds.has(l.id));
  }, [
    pagedLessons,
    studentFilterSelection.ids.join(','),
    studentEnrolledLessonIds,
    lessonMatchesStatusFilter,
  ]);

  const clearFilters = () => {
    const defaults = defaultLessonCalendarFilterSnapshot(defaultLessonFilterYear());
    setSelectedYearFilter(defaults.year);
    setSelectedTermFilter(defaults.term);
    setSelectedLessonTypeFilter(defaults.lessonType);
    setSelectedSkillLevelFilter(defaults.skillLevel);
    setSelectedAgeGroupFilter(defaults.ageGroup);
    setSelectedLocationFilter(defaults.location);
    setCoachFilterSelection({ ids: [], meta: {} });
    setSelectedCourtFilter(defaults.court);
    setSelectedSportFilter(defaults.sport);
    setSelectedActiveFilter(defaults.activeStatus);
    setStudentFilterSelection({ ids: [], meta: {} });
    setStudentSearchQuery('');
    setCoachSearchQuery('');
    setCoachFilterResultsOpen(false);
    setStudentFilterResultsOpen(false);
    setSortBy(defaults.sortBy);
    setSortOrder(defaults.sortOrder);
    if (currentAcademy?.id) {
      writeLessonCalendarFilterSnapshot(currentAcademy.id, defaults);
    }
  };

  const studentSearchResultsMerged = useMemo(() => {
    const byId = new Map<string, StudentProfile>();
    for (const s of studentPickerStudents) byId.set(s.id, s);
    for (const id of studentFilterSelection.ids) {
      const row = studentFilterSelection.meta[id];
      if (row && !byId.has(id)) byId.set(id, row);
    }
    return Array.from(byId.values());
  }, [studentPickerStudents, studentFilterSelection]);

  const coachSearchResultsMerged = useMemo(() => {
    const byId = new Map<string, Coach>();
    for (const c of coachPickerCoaches) byId.set(c.id, c);
    for (const id of coachFilterSelection.ids) {
      const row = coachFilterSelection.meta[id];
      if (row && !byId.has(id)) byId.set(id, row);
    }
    return Array.from(byId.values());
  }, [coachPickerCoaches, coachFilterSelection]);

  const coachSearchLoadingVisible =
    coachSearchDebounced.trim().length >= 1 && coachSearchLoading;
  const studentSearchLoadingVisible =
    studentSearchDebounced.trim().length >= 1 && studentSearchLoading;

  const coachFilterControl = useMemo(
    () => (
      <div ref={coachFilterWrapRef}>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Coach</label>
        <div className="relative mb-2">
          <Input
            placeholder="Search coaches by name…"
            value={coachSearchQuery}
            onChange={(e) => {
              setCoachSearchQuery(e.target.value);
              setCoachFilterResultsOpen(true);
            }}
            onFocus={() => setCoachFilterResultsOpen(true)}
            className="pr-10"
            aria-label="Search coaches for lesson filter"
            aria-autocomplete="list"
            aria-expanded={coachFilterResultsOpen}
            aria-controls="lesson-filter-coach-results"
          />
          {coachSearchLoadingVisible && (
            <Loader2
              className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground"
              aria-hidden
            />
          )}
        </div>
        {coachFilterResultsOpen && coachSearchDebounced.trim().length >= 1 && (
          <ul
            id="lesson-filter-coach-results"
            role="listbox"
            aria-multiselectable="true"
            className="mb-2 max-h-48 overflow-y-auto rounded-md border border-border bg-card shadow-md [scrollbar-gutter:stable]"
          >
            {coachSearchResultsMerged.length === 0 && !coachSearchLoadingVisible ? (
              <li className="px-3 py-2 text-sm text-muted-foreground">No coaches match.</li>
            ) : (
              coachSearchResultsMerged.map((c) => {
                const checked = coachFilterSelection.ids.includes(c.id);
                return (
                  <li key={c.id} role="option" aria-selected={checked}>
                    <label className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-muted">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleCoachFilterRow(c)}
                        className="h-4 w-4 shrink-0 rounded border-input text-primary"
                        aria-label={`Filter by coach ${c.firstName} ${c.lastName}`}
                      />
                      <span className="font-medium text-foreground">
                        {c.firstName} {c.lastName}
                      </span>
                    </label>
                  </li>
                );
              })
            )}
          </ul>
        )}
        {coachFilterSelection.ids.length > 0 ? (
          <p className="mb-1 text-xs text-muted-foreground">
            {coachFilterSelection.ids.length} coach{coachFilterSelection.ids.length !== 1 ? 'es' : ''} selected
            {coachFilterSelection.ids.length <= 3
              ? `: ${coachFilterSelection.ids
                  .map((id) => {
                    const m = coachFilterSelection.meta[id];
                    return m ? `${m.firstName} ${m.lastName}` : id.slice(0, 8);
                  })
                  .join(', ')}`
              : ''}
            . Use Clear Filters to reset.
          </p>
        ) : null}
        <p className="mt-1 text-xs text-muted-foreground">Search and tick one or more coaches.</p>
      </div>
    ),
    [
      coachSearchQuery,
      coachSearchLoadingVisible,
      coachSearchDebounced,
      coachFilterResultsOpen,
      coachSearchResultsMerged,
      coachFilterSelection,
      toggleCoachFilterRow,
    ]
  );

  const studentFilterControl = useMemo(
    () => (
      <div ref={studentFilterWrapRef}>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Student</label>
        <div className="relative mb-2">
          <Input
            placeholder="Search students by name…"
            value={studentSearchQuery}
            onChange={(e) => {
              setStudentSearchQuery(e.target.value);
              setStudentFilterResultsOpen(true);
            }}
            onFocus={() => setStudentFilterResultsOpen(true)}
            className="pr-10"
            aria-label="Search students for calendar filter"
            aria-autocomplete="list"
            aria-expanded={studentFilterResultsOpen}
            aria-controls="lesson-filter-student-results"
          />
          {studentSearchLoadingVisible && (
            <Loader2
              className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground"
              aria-hidden
            />
          )}
        </div>
        {studentFilterResultsOpen && studentSearchDebounced.trim().length >= 1 && (
          <ul
            id="lesson-filter-student-results"
            role="listbox"
            aria-multiselectable="true"
            className="mb-2 max-h-48 overflow-y-auto rounded-md border border-border bg-card shadow-md [scrollbar-gutter:stable]"
          >
            {studentSearchResultsMerged.length === 0 && !studentSearchLoadingVisible ? (
              <li className="px-3 py-2 text-sm text-muted-foreground">No students match.</li>
            ) : (
              studentSearchResultsMerged.map((s) => {
                const checked = studentFilterSelection.ids.includes(s.id);
                return (
                  <li key={s.id} role="option" aria-selected={checked}>
                    <label className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-muted">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleStudentFilterRow(s)}
                        className="h-4 w-4 shrink-0 rounded border-input text-primary"
                        aria-label={`Filter by student ${s.firstName} ${s.lastName}`}
                      />
                      <span className="font-medium text-foreground">
                        {s.firstName} {s.lastName}
                      </span>
                    </label>
                  </li>
                );
              })
            )}
          </ul>
        )}
        {studentFilterSelection.ids.length > 0 ? (
          <p className="mb-1 text-xs text-muted-foreground">
            {studentFilterSelection.ids.length} student{studentFilterSelection.ids.length !== 1 ? 's' : ''} selected
            {studentFilterSelection.ids.length <= 3
              ? `: ${studentFilterSelection.ids
                  .map((id) => {
                    const m = studentFilterSelection.meta[id];
                    return m ? `${m.firstName} ${m.lastName}` : id.slice(0, 8);
                  })
                  .join(', ')}`
              : ''}
            . Use Clear Filters to reset.
          </p>
        ) : null}
        <p className="mt-1 text-xs text-muted-foreground">Search and tick one or more students (lessons where any of them are enrolled).</p>
      </div>
    ),
    [
      studentSearchQuery,
      studentSearchLoadingVisible,
      studentSearchDebounced,
      studentFilterResultsOpen,
      studentSearchResultsMerged,
      studentFilterSelection,
      toggleStudentFilterRow,
    ]
  );

  const lessonFiltersPanel = (
    <div className="space-y-4">
      <h3 className="font-semibold text-gray-900 dark:text-white">Calendar filters</h3>
      {sourceAccess.availableSourceTypes.length > 1 ? (
        <ScheduleSourceTypeFilterBar
          availableSourceTypes={sourceAccess.availableSourceTypes}
          activeSourceFilters={activeSourceFilters}
          onToggle={toggleSource}
          label="Show on calendar"
        />
      ) : null}
      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Lessons</h4>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Year</label>
          <select
            value={selectedYearFilter}
            onChange={(e) => setSelectedYearFilter(e.target.value)}
            className="w-full input"
          >
            <option value="all">All Years</option>
            {yearSelectOptions.map((year) => (
              <option key={year} value={year.toString()}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Term</label>
          <select
            value={selectedTermFilter}
            onChange={(e) => setSelectedTermFilter(e.target.value)}
            className="w-full input"
          >
            <option value="all">All Terms</option>
            {filteredTerms.map((term) => (
              <option key={term.id} value={term.id}>
                {term.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Type</label>
          <select
            value={selectedLessonTypeFilter}
            onChange={(e) => setSelectedLessonTypeFilter(e.target.value)}
            className="w-full input"
          >
            <option value="all">All Types</option>
            <option value="group">Group</option>
            <option value="private">Private</option>
            <option value="camp">Camp</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Skill Level</label>
          <select
            value={selectedSkillLevelFilter}
            onChange={(e) => setSelectedSkillLevelFilter(e.target.value)}
            className="w-full input"
          >
            <option value="all">All Levels</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Age Group</label>
          <select
            value={selectedAgeGroupFilter}
            onChange={(e) => setSelectedAgeGroupFilter(e.target.value)}
            className="w-full input"
          >
            <option value="all">All Ages</option>
            <option value="juniors">Juniors</option>
            <option value="adults">Adults</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
          <select
            value={selectedActiveFilter}
            onChange={(e) => setSelectedActiveFilter(e.target.value)}
            className="w-full input"
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        {locations.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Location</label>
            <select
              value={selectedLocationFilter}
              onChange={(e) => setSelectedLocationFilter(e.target.value)}
              className="w-full input"
            >
              <option value="all">All locations</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </select>
          </div>
        )}
        {coachFilterControl}
        {studentFilterControl}
        {academySports.filter((s) => s.isActive).length > 1 ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Sport</label>
            <select
              value={selectedSportFilter}
              onChange={(e) => setSelectedSportFilter(e.target.value)}
              className="w-full input"
            >
              <option value="all">All sports</option>
              {academySports
                .filter((s) => s.isActive)
                .map((sport) => (
                  <option key={sport.id} value={sport.id}>
                    {getAcademySportLabel(sport)}
                  </option>
                ))}
            </select>
          </div>
        ) : null}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Court</label>
          <select
            value={selectedCourtFilter}
            onChange={(e) => setSelectedCourtFilter(e.target.value)}
            className="w-full input"
          >
            <option value="all">All Courts</option>
            {courts.map((court) => (
              <option key={court.id} value={court.id}>
                {court.name}
              </option>
            ))}
          </select>
        </div>
        {viewMode === 'list' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Sort by</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                className="w-full input"
              >
                <option value="name">Name</option>
                <option value="type">Type</option>
                <option value="price">Price</option>
                <option value="day">Day of Week</option>
                <option value="time">Time</option>
                <option value="enrollment">Enrollment</option>
                <option value="term">Term</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Order</label>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="w-full flex items-center justify-center space-x-2"
              >
                {sortOrder === 'asc' ? (
                  <>
                    <ArrowUp className="h-4 w-4" />
                    <span>Asc</span>
                  </>
                ) : (
                  <>
                    <ArrowDown className="h-4 w-4" />
                    <span>Desc</span>
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </div>
      <Button variant="outline" size="sm" onClick={clearFilters} className="w-full">
        Clear Filters
      </Button>
    </div>
  );

  const defaultYear = defaultLessonFilterYear();
  const hasActiveSourceTypeFilter =
    sourceAccess.availableSourceTypes.length > 1 &&
    activeSourceFilters.length < sourceAccess.availableSourceTypes.length;
  const hasActiveFilters =
    hasActiveSourceTypeFilter ||
    selectedYearFilter === 'all' ||
    selectedYearFilter !== defaultYear ||
    selectedTermFilter !== 'all' ||
    selectedLessonTypeFilter !== 'all' ||
    selectedSkillLevelFilter !== 'all' ||
    selectedAgeGroupFilter !== 'all' ||
    selectedLocationFilter !== 'all' ||
    coachFilterSelection.ids.length > 0 ||
    selectedCourtFilter !== 'all' ||
    selectedSportFilter !== 'all' ||
    selectedActiveFilter !== 'active' ||
    studentFilterSelection.ids.length > 0;

  const locationScopedCourts = useMemo(() => {
    const activeCourts = courts.filter((c) => c.isActive !== false);
    if (selectedLocationFilter === 'all') return activeCourts;
    return activeCourts.filter((c) => c.locationId === selectedLocationFilter);
  }, [courts, selectedLocationFilter]);

  const dayViewCourts = useMemo(() => {
    if (sourceAccess.hasFacilityLayer && facilityLayer.clubCourts.length > 0) {
      const active = facilityLayer.clubCourts;
      if (selectedLocationFilter === 'all') return active;
      return active.filter((c) => c.locationId === selectedLocationFilter);
    }
    return locationScopedCourts;
  }, [
    sourceAccess.hasFacilityLayer,
    facilityLayer.clubCourts,
    selectedLocationFilter,
    locationScopedCourts,
  ]);

  const dayScheduleOccurrences = useMemo(() => {
    const all = buildAcademyOccurrencesFromLessonSessions(
      sortedLessons,
      terms,
      lessonSessions,
      makeupSessions,
      schedulePendingBookings,
      reportedAbsenceSessions,
      courtNamesById,
      undefined,
      scheduleActiveStatusFilter,
      scheduleEnrollmentBookings,
      removedFromSessionEnrollments
    );
    const dayLessons = all.filter((occ) => isSameDay(occ.date, selectedDayDate));
    const merged = [...dayLessons, ...facilityExtraOccurrences.filter((occ) => isSameDay(occ.date, selectedDayDate))];
    if (!activeSourceFilters.length) return merged;
    const allowed = new Set(activeSourceFilters);
    return merged.filter((occ) => allowed.has(occ.sourceType));
  }, [
    sortedLessons,
    terms,
    lessonSessions,
    makeupSessions,
    schedulePendingBookings,
    reportedAbsenceSessions,
    courtNamesById,
    scheduleActiveStatusFilter,
    scheduleEnrollmentBookings,
    removedFromSessionEnrollments,
    selectedDayDate,
    facilityExtraOccurrences,
    activeSourceFilters,
  ]);

  const handleDayDateChange = useCallback(
    (nextDate: Date) => {
      setSelectedDayDate(nextDate);
      const start = new Date(nextDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(nextDate);
      end.setHours(23, 59, 59, 999);
      void fetchOverridesForRange(start, end);
    },
    [fetchOverridesForRange]
  );

  const commitDayReschedule = useCallback(
    async (payload: DayCourtReschedulePayload, notifyGuardians: boolean, reason?: string) => {
      if (!currentAcademy?.id || !payload.occurrence.sessionId) {
        toast.error('This session cannot be rescheduled yet');
        return;
      }
      setDayRescheduleSaving(true);
      try {
        await commitDayScheduleDrag({
          payload,
          academyId: currentAcademy.id,
          notifyGuardians,
          reason,
        });
        await refreshOwnerScheduleAfterMutation(currentAcademy.id);
        toast.success('Session updated');
        setPendingDayReschedule(null);
      } catch (err) {
        toast.error(formatSchedulingErrorMessage(err, 'Failed to update session'));
      } finally {
        setDayRescheduleSaving(false);
      }
    },
    [currentAcademy?.id, refreshOwnerScheduleAfterMutation]
  );

  const handleDayOccurrenceReschedule = useCallback(
    (payload: DayCourtReschedulePayload) => {
      const { needsConfirmDialog } = getSessionRescheduleConfirmMeta(payload, currentAcademy);
      if (needsConfirmDialog) {
        setPendingDayReschedule(payload);
        return;
      }
      void commitDayReschedule(payload, false);
    },
    [commitDayReschedule, currentAcademy]
  );

  const pendingDayRescheduleMeta = pendingDayReschedule
    ? getSessionRescheduleConfirmMeta(pendingDayReschedule, currentAcademy)
    : null;

  const scheduleDataReady = !!currentAcademy?.id && !lessonsLoading && !termsLoading;

  useAutoPageTour('academy-lessons', scheduleDataReady && !embedded);

  const lessonHeaderActions = (
    <div className="flex shrink-0 items-center gap-2">
      <div id={ACADEMY_PAGE_TOUR_STEP_IDS.CALENDAR_VIEW}>
        <ScheduleViewDropdown value={viewMode} onChange={setViewMode} />
      </div>
      <Button
        id={ACADEMY_PAGE_TOUR_STEP_IDS.CALENDAR_CREATE}
        type="button"
        size="sm"
        onClick={() => openCreateLessonForm()}
        disabled={!hasValidSubscription}
        title={!hasValidSubscription ? tooltipMessage : undefined}
        className="h-8 shrink-0 whitespace-nowrap"
      >
        <span className="sm:hidden">Quick</span>
        <span className="hidden sm:inline">Quick Lesson</span>
      </Button>
    </div>
  );

  const lessonHeaderSubheader = (
    <LessonScheduleSubheaderToolbar
      filterSlot={
        <div id={ACADEMY_PAGE_TOUR_STEP_IDS.CALENDAR_FILTERS}>
          <CalendarFiltersDropdown
            hasActiveFilters={hasActiveFilters}
            panelLabel="Calendar filters"
          >
            {lessonFiltersPanel}
          </CalendarFiltersDropdown>
        </div>
      }
      lessonCount={{ filtered: sortedLessons.length, total: lessons.length }}
      calendarNav={
        viewMode === 'monthly' ? (
          <LessonCalendarMonthNav
            currentMonth={calendarMonth}
            onCurrentMonthChange={setCalendarMonth}
            onMonthChange={handleCalendarMonthNavigate}
          />
        ) : viewMode === 'weekly' ? (
          <ScheduleWeekNav
            weekStart={calendarWeekStart}
            onWeekStartChange={setCalendarWeekStart}
            onWeekChange={handleCalendarWeekNavigate}
          />
        ) : viewMode === 'day' ? (
          <div className="inline-flex min-w-0 shrink-0 items-center gap-2">
            <ScheduleDayNav
              selectedDate={selectedDayDate}
              onDateChange={handleDayDateChange}
            />
            {hasActiveCoaches ? (
              <DayColumnAxisToggle value={dayColumnAxis} onChange={setDayColumnAxis} />
            ) : null}
          </div>
        ) : null
      }
    />
  );

  if (!user || !hasAcademyManagementRole(user)) {
    return null;
  }

  // Academy data may still be loading from preload
  if (!currentAcademy) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-0">
      <div className="contents">
        {!embedded ? (
          <PageHeader
            title="Calendar"
            help={
              <PageFeatureHelp
                title="Lesson calendar"
                tours={[
                  {
                    tourKey: 'academy-lessons',
                    label: 'Tour this page',
                    description: 'Views, creating lessons, and filters on your schedule.',
                  },
                ]}
              >
                <p>
                  Monthly, weekly, day, and list views for lessons, camps, and court blocks across
                  your academy.
                </p>
              </PageFeatureHelp>
            }
            helpAnchorId={ACADEMY_PAGE_TOUR_STEP_IDS.CALENDAR_GUIDE}
            actions={lessonHeaderActions}
            subheader={lessonHeaderSubheader}
          />
        ) : null}

        <Drawer
          isOpen={showForm}
          onClose={postCreateLesson ? finishPostCreate : handleCancelEdit}
          title={
            postCreateLesson
              ? 'Add students'
              : editingLesson
                ? 'Edit Lesson'
                : 'Quick create lesson'
          }
          width="3xl"
          footer={
            postCreateLesson ? (
              postCreateDrawerFooter ?? (
                <div className="flex justify-end">
                  <Button type="button" size="sm" onClick={() => void finishPostCreate()}>
                    Done
                  </Button>
                </div>
              )
            ) : (
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={handleCancelEdit}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  form="lesson-form"
                  isLoading={submitting || uploadingCover}
                  disabled={!hasValidSubscription || uploadingCover}
                  title={!hasValidSubscription ? tooltipMessage : undefined}
                >
                  {editingLesson ? 'Update Lesson' : 'Create Lesson'}
                </Button>
              </div>
            )
          }
        >
          <>
                  {postCreateLesson && currentAcademy?.slug ? (
                    <PostCreateLessonStep
                      academyId={currentAcademy.id}
                      academySlug={currentAcademy.slug}
                      lessonId={postCreateLesson.id}
                      lessonName={postCreateLesson.name}
                      maxCapacity={postCreateLesson.maxCapacity}
                      onInlineFooterChange={setPostCreateDrawerFooter}
                      onEnrollSuccess={refreshCalendarAfterOverride}
                      onDone={() => void finishPostCreate()}
                    />
                  ) : (
                  <form id="lesson-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {!editingLesson ? (
                      <QuickLessonCreateForm
                        register={register}
                        control={control}
                        academyId={currentAcademy?.id}
                        errors={errors}
                        formDetailLevel={formDetailLevel}
                        onFormDetailLevelChange={setFormDetailLevel}
                        syncPrivateSessionDatesToForm={syncPrivateSessionDatesToForm}
                        watchedPrivateSessionDates={watchedPrivateSessionDates}
                        watchedStartDate={watchedStartDate}
        watchedPrice={watchedPrice}
                        hasMultiLocation={hasMultiLocation}
                        locations={locations}
                        activeCourtsForPicker={activeCourtsForPicker}
                        selectedCourtIds={selectedCourtIds}
                        toggleCourtSelection={toggleCourtSelection}
                        coaches={coaches}
                        coachesLoading={coachesLoading}
                        selectedCoachIds={selectedCoachIds}
                        toggleCoachSelection={toggleCoachSelection}
                        academyAgeGroups={academyAgeGroups}
                        hasProgramsAccess={hasProgramsAccess}
                        programs={programsForLessonForm}
                        watchedProgramId={watchedProgramId}
                        onApplyProgramTemplate={applyProgramTemplate}
                        programTemplateHelperText={programTemplateHelperText}
                        currencySymbol={currencySymbol}
                        academyCurrency={academyCurrency}
                        academyRegion={currentAcademy?.region}
                        coverDisplayUrl={coverDisplayUrl}
                        uploadingCover={uploadingCover}
                        submitting={submitting}
                        onCoverFile={handleCoverFile}
                        onCoverRemove={handleCoverRemove}
                      />
                    ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <>
                      {/* Lesson Type - First Field */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Lesson Type *
                        </label>
                        <select
                          {...register('lessonType')}
                          className="input"
                        >
                          <option value="group">Group Lesson</option>
                          <option value="private">Private Lesson</option>
                          <option value="camp">Camp</option>
                        </select>
                        {errors.lessonType && (
                          <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                            {errors.lessonType.message}
                          </p>
                        )}
                      </div>

                      <AcademySportSelectField
                        academyId={currentAcademy?.id}
                        control={control}
                        name="academySportId"
                        required
                        className="md:col-span-2"
                      />

                      {/* Terms - Group lessons run within program date ranges */}
                      {watchedLessonType === 'group' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Program / term * ({selectedTermIds.length} selected)
                          </label>
                          <div className="max-h-32 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md p-2 bg-white dark:bg-gray-800">
                            {termsForLessonForm.length === 0 ? (
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                No current or upcoming programs — create a new term to schedule lessons.
                              </p>
                            ) : (
                              termsForLessonForm.map((term) => (
                                <label key={term.id} className="flex items-center space-x-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-700 rounded px-2">
                                  <input
                                    type="checkbox"
                                    checked={selectedTermIds.includes(term.id)}
                                    onChange={() => toggleTermSelection(term.id)}
                                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                                  />
                                  <span className="text-sm text-gray-700 dark:text-gray-300">{term.name}</span>
                                </label>
                              ))
                            )}
                          </div>
                          {errors.termIds && (
                            <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                              {errors.termIds.message}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Private lesson scheduling: standalone package vs program/term */}
                      {watchedLessonType === 'private' && (
                        <>
                          <div className="md:col-span-2">
                            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Scheduling *
                            </span>
                            <div className="space-y-2">
                              <label className="flex items-start gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name="privateScheduleMode"
                                  className="mt-1"
                                  checked={!isPrivateTermLinked}
                                  onChange={() => {
                                    setValue('termIds', [], { shouldValidate: true });
                                  }}
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                  <span className="font-medium">Standalone package</span>
                                  {' — '}
                                  Pick each session date on the calendar below (bookable as a fixed package).
                                </span>
                              </label>
                              <label className="flex items-start gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name="privateScheduleMode"
                                  className="mt-1"
                                  checked={isPrivateTermLinked}
                                  onChange={() => {
                                    setValue('startDate', '', { shouldValidate: true });
                                    setValue('numberOfSessions', undefined, { shouldValidate: true });
                                    setValue('privateSessionDates', [], { shouldValidate: true });
                                    const firstTerm = termsForLessonForm[0];
                                    setValue('termIds', firstTerm ? [firstTerm.id] : [], {
                                      shouldValidate: true,
                                    });
                                  }}
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                  <span className="font-medium">Part of a program / term</span>
                                  {' — '}
                                  Runs on the selected weekdays for the full program dates (1-on-1 within the term).
                                </span>
                              </label>
                            </div>
                          </div>

                          {isPrivateTermLinked ? (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Program / term *
                              </label>
                              <select
                                className="input"
                                value={selectedTermIds[0] ?? ''}
                                onChange={(e) => {
                                  const id = e.target.value;
                                  setValue('termIds', id ? [id] : [], { shouldValidate: true });
                                }}
                              >
                                <option value="">Select a program…</option>
                                {termsForLessonForm.map((term) => (
                                  <option key={term.id} value={term.id}>
                                    {term.name}
                                  </option>
                                ))}
                              </select>
                              {errors.termIds && (
                                <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                                  {errors.termIds.message}
                                </p>
                              )}
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Sessions follow the program start and end dates on the days of week selected below.
                              </p>
                            </div>
                          ) : (
                            <div className="md:col-span-2">
                              <PrivatePackageSessionDatesField
                                selectedDates={sortPrivateSessionDates(watchedPrivateSessionDates)}
                                onChange={syncPrivateSessionDatesToForm}
                                initialFocusDate={
                                  watchedPrivateSessionDates[0]
                                    ? parseLocalDateYmd(watchedPrivateSessionDates[0]) ?? undefined
                                    : watchedStartDate
                                      ? parseLocalDateYmd(watchedStartDate) ?? undefined
                                      : undefined
                                }
                                region={currentAcademy?.region}
                              />
                              {errors.termIds && sortPrivateSessionDates(watchedPrivateSessionDates).length === 0 ? (
                                <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                                  {errors.termIds.message}
                                </p>
                              ) : null}
                            </div>
                          )}
                        </>
                      )}

                      {/* Camp: week schedule (matches Camp Management single camp form) */}
                      {watchedLessonType === 'camp' && (
                        <CampLessonScheduleFields
                          register={register}
                          errors={errors}
                          selectedDaysOfWeek={selectedDaysOfWeek}
                          onToggleDay={toggleDaySelection}
                          computedWeekDates={computedCampWeekDates}
                          region={currentAcademy?.region}
                          showRepeatWeekly={!editingLesson}
                          weekCount={campWeekCount}
                          onWeekCountChange={setCampWeekCount}
                          campNameForPreview={watchedCampName}
                        />
                      )}

                      {hasMultiLocation && locations.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Location (optional)
                          </label>
                          <select {...register('locationId')} className="input">
                            <option value="">All locations — show all courts</option>
                            {locations
                              .filter((l) => l.isActive)
                              .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
                              .map((loc) => (
                                <option key={loc.id} value={loc.id}>
                                  {loc.name}
                                </option>
                              ))}
                          </select>
                          {watchedLocationId ? (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Only courts at this location are listed below.
                            </p>
                          ) : null}
                          {errors.locationId && (
                            <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.locationId.message}</p>
                          )}
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Courts * ({selectedCourtIds.length} selected)
                        </label>
                        <div className="max-h-32 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md p-2 bg-white dark:bg-gray-800">
                          {activeCourtsForPicker.length === 0 ? (
                            <p className="text-sm text-gray-500 dark:text-gray-400">No active courts available</p>
                          ) : (
                            activeCourtsForPicker.map((court) => (
                              <label key={court.id} className="flex items-center space-x-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-700 rounded px-2">
                                <input
                                  type="checkbox"
                                  checked={selectedCourtIds.includes(court.id)}
                                  onChange={() => toggleCourtSelection(court.id)}
                                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">{court.name}</span>
                              </label>
                            ))
                          )}
                        </div>
                        {errors.courtIds && (
                          <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                            {errors.courtIds.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Coaches * ({selectedCoachIds.length} selected)
                        </label>
                        <div className="max-h-32 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md p-2 bg-white dark:bg-gray-800">
                          {coachesLoading ? (
                            <p className="text-sm text-muted-foreground flex items-center gap-2">
                              <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden />
                              Loading coaches…
                            </p>
                          ) : coaches.filter(coach => coach.isActive).length === 0 ? (
                            <p className="text-sm text-gray-500 dark:text-gray-400">No active coaches available</p>
                          ) : (
                            coaches.filter(coach => coach.isActive).map((coach) => (
                              <label key={coach.id} className="flex items-center space-x-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-700 rounded px-2">
                                <input
                                  type="checkbox"
                                  checked={selectedCoachIds.includes(coach.id)}
                                  onChange={() => toggleCoachSelection(coach.id)}
                                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                  {coach.firstName} {coach.lastName}
                                </span>
                              </label>
                            ))
                          )}
                        </div>
                        {errors.coachIds && (
                          <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                            {errors.coachIds.message}
                          </p>
                        )}
                      </div>

                      {academyAgeGroups.length > 0 ? (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Age Group
                          </label>
                          <select
                            {...register('ageGroupId')}
                            className="input"
                          >
                            <option value="">— None —</option>
                            {academyAgeGroups.map((ag) => (
                              <option key={ag.id} value={ag.id}>
                                {ag.label}
                              </option>
                            ))}
                          </select>
                          {errors.ageGroupId && (
                            <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                              {(errors.ageGroupId as any).message}
                            </p>
                          )}
                        </div>
                      ) : (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Age Group
                            </label>
                            <select
                              {...register('ageGroup')}
                              className="input"
                            >
                              <option value="all">All Ages</option>
                              <option value="juniors">Juniors</option>
                              <option value="adults">Adults</option>
                            </select>
                            {errors.ageGroup && (
                              <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                                {errors.ageGroup.message}
                              </p>
                            )}
                          </div>

                        </>
                      )}

                      <Field data-invalid={!!errors.name?.message}>
                        <FieldLabel htmlFor="name">
                          {watchedLessonType === 'camp' ? 'Camp Name *' : 'Lesson Name *'}
                        </FieldLabel>
                        <Input
                          id="name"
                          aria-invalid={!!errors.name?.message}
                          {...register('name')}
                          placeholder={watchedLessonType === 'camp' ? 'Summer Tennis Camp' : 'Beginner Group Lesson'}
                        />
                        <FieldError>{errors.name?.message}</FieldError>
                      </Field>

                      {hasProgramsAccess ? (
                        <Field className="md:col-span-2" data-invalid={!!errors.programId?.message}>
                          <FieldLabel htmlFor="programId" className="flex items-center gap-2">
                            <Layers className="h-4 w-4 text-muted-foreground" aria-hidden />
                            Development program
                          </FieldLabel>
                          <select
                            id="programId"
                            aria-invalid={!!errors.programId?.message}
                            className="input"
                            value={watchedProgramId ?? ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              applyProgramTemplate(v ? v : null);
                            }}
                          >
                            <option value="">None</option>
                            {[...programsForLessonForm]
                              .sort((a, b) => {
                                if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
                                return a.name.localeCompare(b.name);
                              })
                              .map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.name}
                                  {!p.isActive ? ' (inactive)' : ''}
                                </option>
                              ))}
                          </select>
                          <FieldError>{errors.programId?.message}</FieldError>
                          <p className="text-xs text-muted-foreground mt-1">
                            {programTemplateHelperText}
                          </p>
                        </Field>
                      ) : null}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Skill Level
                        </label>
                        <select
                          {...register('skillLevel')}
                          className="input"
                        >
                          <option value="any">Any Level</option>
                          <option value="beginner">Beginner</option>
                          <option value="intermediate">Intermediate</option>
                          <option value="advanced">Advanced</option>
                        </select>
                        {errors.skillLevel && (
                          <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                            {errors.skillLevel.message}
                          </p>
                        )}
                      </div>

                      {!(watchedLessonType === 'private' && !isPrivateTermLinked) && watchedLessonType !== 'camp' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Days of Week * ({selectedDaysOfWeek.length} selected)
                        </label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {DAYS_OF_WEEK.map((day, index) => (
                            <label
                              key={index}
                              className={`flex items-center space-x-2 p-2 border rounded-md cursor-pointer transition-colors ${selectedDaysOfWeek.includes(index)
                                ? 'bg-primary-50 border-primary-500 dark:bg-primary-900 dark:border-primary-400'
                                : 'bg-white border-gray-300 dark:bg-gray-800 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                                }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedDaysOfWeek.includes(index)}
                                onChange={() => toggleDaySelection(index)}
                                className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                                {day.substring(0, 3)}
                              </span>
                            </label>
                          ))}
                        </div>
                        {selectedDaysOfWeek.length === 0 && (
                          <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                            Please select at least one day
                          </p>
                        )}
                        {errors.daysOfWeek && (
                          <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                            {errors.daysOfWeek.message}
                          </p>
                        )}
                      </div>
                      )}

                      <Field data-invalid={!!errors.startTime?.message}>
                        <FieldLabel htmlFor="startTime">Start Time *</FieldLabel>
                        <Input id="startTime" type="time" aria-invalid={!!errors.startTime?.message} {...register('startTime')} />
                        <FieldError>{errors.startTime?.message}</FieldError>
                      </Field>

                      <Field data-invalid={!!errors.endTime?.message}>
                        <FieldLabel htmlFor="endTime">End Time *</FieldLabel>
                        <Input id="endTime" type="time" aria-invalid={!!errors.endTime?.message} {...register('endTime')} />
                        <FieldError>{errors.endTime?.message}</FieldError>
                      </Field>

                      {/* Max Capacity — group (unlimited option) or private (1–50 students) */}
                      {watchedLessonType === 'private' ? (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Max students *
                          </label>
                          <input
                            type="number"
                            min={1}
                            max={50}
                            {...register('maxCapacity', { valueAsNumber: true })}
                            className={`input w-full ${errors.maxCapacity ? 'border-red-500' : ''}`}
                          />
                          {errors.maxCapacity && (
                            <p className="mt-1 text-sm text-red-600">{errors.maxCapacity.message}</p>
                          )}
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            One booking per student. You can enroll multiple students after creating the lesson.
                          </p>
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Maximum Capacity *</label>
                            <label className="flex items-center space-x-2 text-sm">
                              <input
                                type="checkbox"
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                checked={watchedMaxCapacity === -1}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setValue('maxCapacity', -1, { shouldValidate: true });
                                  } else {
                                    setValue('maxCapacity', 6, { shouldValidate: true });
                                  }
                                }}
                              />
                              <span className="text-gray-700 dark:text-gray-300">No student limit</span>
                            </label>
                          </div>
                          {watchedMaxCapacity !== -1 ? (
                            <div className="relative">
                              <input
                                type="number"
                                {...register('maxCapacity', { valueAsNumber: true })}
                                className={`input w-full ${errors.maxCapacity ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                                placeholder="6"
                                min={1}
                              />
                              {errors.maxCapacity && (
                                <p className="mt-1 text-sm text-red-600">{errors.maxCapacity.message}</p>
                              )}
                            </div>
                          ) : (
                            <div className="p-2 border rounded bg-gray-50 text-gray-500 text-sm italic dark:bg-gray-800 dark:border-gray-700">
                              Unlimited students allowed
                            </div>
                          )}
                        </div>
                      )}

                      {watchedLessonType === 'camp' ? (
                        <CampLessonPricingFields
                          register={register}
                          errors={errors}
                          currencySymbol={currencySymbol}
                        />
                      ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Price ({currencySymbol}){' '}
                          {watchedLessonType === 'private'
                            ? isPrivateTermLinked
                              ? 'for term'
                              : 'per session'
                            : ''}{' '}
                          *
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
                            {currencySymbol}
                          </span>
                          <input
                            type="number"
                            step="0.01"
                            onWheel={(e) => e.currentTarget.blur()}
                            {...register('price', { valueAsNumber: true })}
                            className="input pl-12"
                            placeholder="50.00"
                          />
                        </div>
                        {errors.price && (
                          <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                            {errors.price.message}
                          </p>
                        )}
                        {watchedLessonType === 'private' &&
                          !isPrivateTermLinked &&
                          watchedPrice > 0 &&
                          sortPrivateSessionDates(watchedPrivateSessionDates).length > 0 && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            Total package price:{' '}
                            {formatCurrency(
                              watchedPrice * sortPrivateSessionDates(watchedPrivateSessionDates).length,
                              academyCurrency
                            )}{' '}
                            ({sortPrivateSessionDates(watchedPrivateSessionDates).length} sessions ×{' '}
                            {formatCurrency(watchedPrice, academyCurrency)})
                          </p>
                        )}
                      </div>
                      )}

                      {watchedLessonType === 'group' || (watchedLessonType === 'private' && isPrivateTermLinked) ? (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Per-session price ({currencySymbol}){' '}
                            <span className="text-gray-500 font-normal">(optional)</span>
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
                              {currencySymbol}
                            </span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              onWheel={(e) => e.currentTarget.blur()}
                              {...register('sessionPrice', {
                                setValueAs: (v) => {
                                  if (v === '' || v === null || v === undefined) return undefined;
                                  const n = typeof v === 'number' ? v : Number(v);
                                  return Number.isNaN(n) ? undefined : n;
                                },
                              })}
                              className="input pl-12"
                              placeholder="Leave empty for full-term only"
                            />
                          </div>
                          {errors.sessionPrice && (
                            <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                              {errors.sessionPrice.message}
                            </p>
                          )}
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            {watchedLessonType === 'private'
                              ? 'If set, staff and guardians can enroll for a single session instead of the full term.'
                              : 'If set, students can book individual sessions of this lesson instead of the full term.'}
                          </p>
                        </div>
                      ) : null}

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Description
                        </label>
                        <textarea
                          {...register('description')}
                          rows={3}
                          className="input"
                          placeholder="Lesson description and details..."
                        />
                        {errors.description && (
                          <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                            {errors.description.message}
                          </p>
                        )}
                      </div>

                      <div className="md:col-span-2">
                        <LessonCoverImageField
                          displayUrl={coverDisplayUrl}
                          uploading={uploadingCover}
                          disabled={submitting || uploadingCover}
                          inputId="lesson-cover-upload"
                          onFileSelect={handleCoverFile}
                          onRemove={handleCoverRemove}
                        />
                        <input type="hidden" {...register('imageUrl')} />
                      </div>

                      <div className="md:col-span-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            {...register('isActive')}
                            className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                          />
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                            Active (available for booking)
                          </span>
                        </label>
                      </div>

                      <div className="md:col-span-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            {...register('isFeatured')}
                            className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                          />
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                            Featured (show on public academy page - only for active/upcoming terms)
                          </span>
                        </label>
                      </div>
                      </>
                    </div>
                    )}
                  </form>
                  )}

              {!postCreateLesson && (
              <div className="space-y-6 mt-6 border-t border-gray-200 dark:border-gray-700 pt-6">
                {/* Conflict Warning */}
                {conflicts.length > 0 && (
                  <Card>
                    <CardContent className="pt-6">
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-400 dark:border-yellow-600 p-4 rounded-lg">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-yellow-900 dark:text-yellow-200 mb-2">
                              {schedulingConflictSummary.hasActive
                                ? '⚠️ Scheduling Conflict Detected'
                                : 'Schedule overlap with inactive lessons'}
                            </h4>
                            <p className="text-sm text-yellow-800 dark:text-yellow-300 mb-3">
                              {schedulingConflictSummary.hasActive && schedulingConflictSummary.hasInactive
                                ? `Some existing lessons conflict with the lesson you're ${editingLesson ? 'editing' : 'creating'}. Inactive lessons are shown for reference only.`
                                : schedulingConflictSummary.hasInactive
                                  ? `The following inactive lessons overlap in time with the lesson you're ${editingLesson ? 'editing' : 'creating'}. They are not available for booking.`
                                  : `The following existing lessons conflict with the lesson you're ${editingLesson ? 'editing' : 'creating'}:`}
                            </p>
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              {conflicts.map((conflict, index) => {
                                const isInactiveLesson = conflict.lesson.isActive === false;
                                return (
                                <div
                                  key={`${conflict.lesson.id}-${conflict.type}-${index}`}
                                  className={cn(
                                    'bg-white dark:bg-gray-800 p-3 rounded border',
                                    isInactiveLesson
                                      ? 'border-gray-300 dark:border-gray-600'
                                      : 'border-yellow-300 dark:border-yellow-700'
                                  )}
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex flex-wrap items-center gap-1.5">
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                                          {conflict.lesson.name}
                                        </p>
                                        {isInactiveLesson && (
                                          <span className="shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900 dark:text-red-200">
                                            Inactive
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                        <span className="font-medium">{isInactiveLesson ? 'Overlap:' : 'Conflict:'}</span>{' '}
                                        {conflict.type === 'court' ? 'Same court' : 'Same coach'} at the same time
                                        {isInactiveLesson ? ' (inactive — not bookable)' : ''}
                                      </p>
                                      <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 space-y-0.5">
                                        <p>• <span className="font-medium">Term:</span> {conflict.termName}</p>
                                        <p>• <span className="font-medium">Court:</span> {conflict.courtName}</p>
                                        <p>• <span className="font-medium">Coach:</span> {conflict.coachName}</p>
                                        <p>• <span className="font-medium">Time:</span> {(conflict.lesson.daysOfWeek || [conflict.lesson.daysOfWeek]).map(d => DAYS_OF_WEEK[d as number]).join(', ')} {conflict.lesson.startTime} - {conflict.lesson.endTime}</p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                              })}
                            </div>
                            <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-3">
                              {schedulingConflictSummary.hasActive
                                ? `You can still ${editingLesson ? 'save' : 'create'} the lesson${!editingLesson ? 's' : ''}, but this may cause scheduling issues. Please review the conflicts above.`
                                : `You can still ${editingLesson ? 'save' : 'create'} the lesson${!editingLesson ? 's' : ''} — inactive lessons do not block new bookings.`}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Coach Availability Warning */}
                {availabilityWarnings.length > 0 && (
                  <Card>
                    <CardContent className="pt-6">
                      <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-400 dark:border-amber-600 p-4 rounded-lg">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-amber-900 dark:text-amber-200 mb-2">
                              ⚠️ Coach Availability Warning
                            </h4>
                            <p className="text-sm text-amber-800 dark:text-amber-300 mb-3">
                              The following coaches do not have availability set for the selected days and times:
                            </p>
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              {availabilityWarnings.map((warning, index) => (
                                <div key={`${warning.coachId}-${index}`} className="bg-white dark:bg-gray-800 p-3 rounded border border-amber-300 dark:border-amber-700">
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                                      {warning.coachName}
                                    </p>
                                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                      <span className="font-medium">No availability on:</span> {warning.missingDays.map(d => DAYS_OF_WEEK[d]).join(', ')}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <p className="text-xs text-amber-700 dark:text-amber-400 mt-3">
                              You can still {editingLesson ? 'save' : 'create'} the lesson, but you may want to adjust the schedule or update coach availability first.
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {courtHireWarnings.length > 0 && (
                  <Card>
                    <CardContent className="pt-6">
                      <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-400 dark:border-amber-600 p-4 rounded-lg">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-amber-900 dark:text-amber-200 mb-2">
                              Court hire conflict warning
                            </h4>
                            <p className="text-sm text-amber-800 dark:text-amber-300 mb-3">
                              Existing court hire bookings overlap the schedule you are creating:
                            </p>
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              {courtHireWarnings.map((warning, index) => (
                                <div
                                  key={`${warning.courtName}-${warning.bookingDate}-${warning.bookingTime}-${index}`}
                                  className="bg-white dark:bg-gray-800 p-3 rounded border border-amber-300 dark:border-amber-700"
                                >
                                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                                    {warning.courtName}
                                  </p>
                                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                    {warning.bookingDate} at {warning.bookingTime}
                                  </p>
                                </div>
                              ))}
                            </div>
                            <p className="text-xs text-amber-700 dark:text-amber-400 mt-3">
                              You can still {editingLesson ? 'save' : 'create'} the lesson, but the court may already be booked for hire at those times.
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Lesson Creation Preview */}
                {!editingLesson &&
                  ((watchedLessonType === 'private' && watchedStartDate && watchedNumberOfSessions > 0 && selectedCourtIds.length > 0 && selectedCoachIds.length > 0) ||
                    (watchedLessonType !== 'private' && selectedTermIds.length > 0 && selectedCourtIds.length > 0 && selectedCoachIds.length > 0)) && (
                    <Card>
                      <CardContent className="pt-6">
                        <div className="bg-blue-50 dark:bg-blue-900 p-4 rounded-lg space-y-3">
                          <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-3">
                            📋 Lesson Creation Preview
                          </h4>

                          {/* Lesson Count */}
                          <div className="border-b border-blue-200 dark:border-blue-700 pb-2">
                            <p className="text-sm text-blue-800 dark:text-blue-200 mb-1">
                              {watchedLessonType === 'private' ? (
                                <>
                                  <span className="font-semibold text-blue-900 dark:text-blue-100">
                                    1 private lesson
                                  </span> with {selectedCourtIds.length} court(s) and {selectedCoachIds.length} coach(es) will be created
                                </>
                              ) : (
                                <>
                                  <span className="font-semibold text-blue-900 dark:text-blue-100">
                                    {selectedTermIds.length} lesson(s)
                                  </span> (1 per term) with {selectedCourtIds.length} court(s) and {selectedCoachIds.length} coach(es) will be created
                                </>
                              )}
                            </p>
                          </div>

                          {/* Sessions per Term - Only for Group/Clinic */}
                          {watchedLessonType !== 'private' && selectedDaysOfWeek.length > 0 && (
                            <div className="border-b border-blue-200 dark:border-blue-700 pb-2">
                              <p className="text-xs font-medium text-blue-900 dark:text-blue-100 mb-1">
                                Sessions per Term:
                              </p>
                              <div className="space-y-1">
                                {selectedTermIds.map((termId) => {
                                  const term = terms.find(t => t.id === termId);
                                  if (!term || !term.startDate || !term.endDate) {
                                    return (
                                      <p key={termId} className="text-xs text-blue-700 dark:text-blue-300">
                                        {term?.name || 'Unknown'}: Calculating...
                                      </p>
                                    );
                                  }

                                  // Calculate number of sessions (occurrences of the selected day of week between start and end dates)
                                  const startDate = new Date(term.startDate);
                                  const endDate = new Date(term.endDate);
                                  let sessionCount = 0;
                                  const currentDate = new Date(startDate);

                                  while (currentDate <= endDate) {
                                    if (selectedDaysOfWeek.includes(currentDate.getDay())) {
                                      sessionCount++;
                                    }
                                    currentDate.setDate(currentDate.getDate() + 1);
                                  }

                                  return (
                                    <p key={termId} className="text-xs text-blue-700 dark:text-blue-300">
                                      <span className="font-medium">{term.name}:</span> {sessionCount} session{sessionCount !== 1 ? 's' : ''}
                                    </p>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Private Lesson Session Info */}
                          {watchedLessonType === 'private' && watchedStartDate && watchedNumberOfSessions > 0 && (
                            <div className="border-b border-blue-200 dark:border-blue-700 pb-2">
                              <p className="text-xs font-medium text-blue-900 dark:text-blue-100 mb-1">
                                Private Lesson Details:
                              </p>
                              <p className="text-xs text-blue-700 dark:text-blue-300">
                                <span className="font-medium">Start Date:</span> {new Date(watchedStartDate).toLocaleDateString()}
                              </p>
                              <p className="text-xs text-blue-700 dark:text-blue-300">
                                <span className="font-medium">Number of Sessions:</span> {watchedNumberOfSessions}
                              </p>
                              <p className="text-xs text-blue-700 dark:text-blue-300">
                                <span className="font-medium">Days of Week:</span> {selectedDaysOfWeek.map(d => DAYS_OF_WEEK[d]).join(', ')}
                              </p>
                            </div>
                          )}

                          {/* Total Potential Revenue */}
                          {watchedPrice > 0 && (
                            <div className="border-b border-blue-200 dark:border-blue-700 pb-2">
                              <p className="text-xs font-medium text-blue-900 dark:text-blue-100 mb-1">
                                Total Potential Revenue:
                              </p>
                              {(() => {
                                if (watchedLessonType === 'private') {
                                  // Private lessons: 1 lesson, price per session × number of sessions
                                  const totalLessons = 1;
                                  const totalPricePerLesson = watchedPrice * watchedNumberOfSessions;
                                  const totalRevenue = totalLessons * totalPricePerLesson;

                                  return (
                                    <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                                      {formatCurrency(totalRevenue, academyCurrency)}
                                      <span className="text-xs font-normal text-blue-700 dark:text-blue-300 ml-1 block mt-1">
                                        (1 lesson × {formatCurrency(watchedPrice, academyCurrency)}/session × {watchedNumberOfSessions} sessions)
                                      </span>
                                    </p>
                                  );
                                } else {
                                  // Group/Clinic lessons: 1 lesson per term, price × max capacity × number of lessons
                                  if (!watchedMaxCapacity || watchedMaxCapacity === 0) return null;

                                  const totalLessons = selectedTermIds.length;
                                  const revenuePerLesson = watchedPrice * watchedMaxCapacity;
                                  const totalRevenue = totalLessons * revenuePerLesson;

                                  return (
                                    <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                                      {formatCurrency(totalRevenue, academyCurrency)}
                                      <span className="text-xs font-normal text-blue-700 dark:text-blue-300 ml-1 block mt-1">
                                        ({totalLessons} lessons × {formatCurrency(watchedPrice, academyCurrency)} × {watchedMaxCapacity} students)
                                      </span>
                                    </p>
                                  );
                                }
                              })()}
                            </div>
                          )}

                          {/* Info Notes */}
                          <div className="text-xs text-blue-700 dark:text-blue-300 space-y-1 pt-1">
                            <p>• One lesson per term; each lesson can use multiple courts and coaches</p>
                            <p>• All lessons will have the same settings (name, time, price, etc.)</p>
                            <p>• Revenue assumes all sessions are booked at full capacity (all students enrolled)</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
              </div>
              )}
            </>
        </Drawer>

          <div className="w-full">
            {scheduleDataReady ? (
            viewMode === 'day' ? (
              <DayCourtScheduleView
                date={selectedDayDate}
                onDateChange={handleDayDateChange}
                courts={dayViewCourts}
                occurrences={dayScheduleOccurrences}
                academyId={currentAcademy?.id}
                editable
                hideHeader
                columnAxis={dayColumnAxis}
                coaches={coaches}
                sourceTypeFilters={activeSourceFilters}
                onOccurrenceClick={(occ) => {
                  if (occ.sourceType === 'lesson') {
                    handleLessonOccurrenceClick(scheduleOccurrenceToClickPayload(occ));
                  } else if (occ.sourceType === 'play_event') {
                    handleScheduleOccurrenceClick(occ);
                  }
                }}
                onOccurrenceReschedule={handleDayOccurrenceReschedule}
              />
            ) : (
            <AcademyScheduleCalendar
            hideHeader
            lessons={sortedLessons}
            terms={terms}
            overrides={overrides}
            lessonSessions={lessonSessions}
            makeupSessions={makeupSessions}
            pendingScheduleBookings={schedulePendingBookings}
            scheduleEnrollmentBookings={scheduleEnrollmentBookings}
            removedFromSessionEnrollments={removedFromSessionEnrollments}
            reportedAbsenceSessions={reportedAbsenceSessions}
            courtNamesById={courtNamesById}
            coaches={coaches}
            onCopyPrivateLessonLink={copyShareableLink}
            onLessonOccurrenceClick={handleLessonOccurrenceClick}
            onScheduleOccurrenceClick={handleScheduleOccurrenceClick}
            coachAvailabilityWarnings={lessonAvailabilityWarnings}
            viewMode={viewMode}
            onViewModeChange={(v) => setViewMode(v)}
            currentMonth={calendarMonth}
            onCurrentMonthChange={setCalendarMonth}
            weekStart={calendarWeekStart}
            onWeekStartChange={setCalendarWeekStart}
            onMonthChange={handleCalendarMonthNavigate}
            onWeekChange={handleCalendarWeekNavigate}
            showCreateLesson
            onCreateLesson={openCreateLessonForm}
            createLessonTooltip={tooltipMessage}
            createLessonDisabled={!hasValidSubscription}
            programNameById={programNameById}
            scheduleActiveStatusFilter={scheduleActiveStatusFilter}
            showSportGlyphsOnChips={isMultiSportAcademy(academySports)}
            academySports={academySports}
            extraOccurrences={facilityExtraOccurrences}
            sourceTypeFilters={activeSourceFilters}
            scheduleLegend={
              <div className="space-y-2 px-1">
                <AgeGroupCalendarLegend ageGroups={academyAgeGroups} />
                <ScheduleTypeStatusLegend />
              </div>
            }
            loading={loading}
            emptyContent={
              sortedLessons.length === 0 ? (
                <DashboardEmptyStateCard>
                  <DashboardEmptyStateIcon icon={CalendarDays} />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    {lessons.length === 0 ? 'No lessons yet' : 'No lessons match your filters'}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {lessons.length === 0
                      ? courts.length === 0 || missingCoachesForSetup || terms.length === 0
                        ? 'Create courts, coaches, and terms first'
                        : termsForLessonForm.length === 0
                          ? 'Add a current or upcoming term for group lessons. Private and camp lessons do not use this term list here.'
                          : 'Create your first lesson to get started'
                      : `Try adjusting your filters. (${lessons.length} total lessons)`
                    }
                  </p>
                  {lessons.length > 0 && hasActiveFilters && (
                    <Button variant="outline" onClick={clearFilters} className="mb-4">
                      Clear Filters
                    </Button>
                  )}
                  {lessons.length === 0 && courts.length > 0 && !missingCoachesForSetup && (
                    <div className="flex flex-col items-center space-y-4">
                      {!currentAcademy?.stripeConnectAccountId && hasPaymentProcessingAccess ? (
                        <>
                          <Button
                            onClick={() => navigate('/admin/payments?tab=settings')}
                            className="bg-yellow-600 hover:bg-yellow-700 text-white"
                          >
                            Set Up Stripe Connect to Create Lessons
                          </Button>
                          <p className="text-sm text-gray-600 dark:text-gray-400 max-w-md text-center">
                            You need to set up a Stripe Connect account before creating lessons to accept payments securely.
                          </p>
                        </>
                      ) : !currentAcademy?.stripeConnectAccountId && !hasPaymentProcessingAccess ? (
                          <Button
                            onClick={() => openCreateLessonForm()}
                            disabled={!hasValidSubscription}
                            title={!hasValidSubscription ? tooltipMessage : undefined}
                          >
                            Create Lesson
                          </Button>
                      ) : (
                          <Button
                            onClick={() => openCreateLessonForm()}
                            disabled={!hasValidSubscription}
                            title={!hasValidSubscription ? tooltipMessage : undefined}
                          >
                            Create Lesson
                          </Button>
                      )}
                    </div>
                  )}
                </DashboardEmptyStateCard>
              ) : undefined
            }
            listContent={
              <div className="space-y-8">
                {isSourceActive('lesson') ? (
                  <PaginatedList
                    data={listLessonsForStudentFilter}
                    pagination={listPagination}
                    loading={listLoading}
                    emptyMessage="No lessons found. Try adjusting your filters."
                    onPageChange={fetchListPage}
                    renderItem={(lesson) => (
                    <div className="rounded-lg border border-border bg-card p-3 text-card-foreground shadow-sm transition-shadow hover:shadow-md sm:p-5">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <h3 className="w-full truncate text-base font-semibold text-gray-900 dark:text-white sm:w-auto sm:text-lg">
                              {lesson.name}
                            </h3>
                            {lessonAvailabilityWarningSet.has(lesson.id) && (
                              <span title="Coach availability warning" className="inline-flex shrink-0">
                                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" aria-hidden />
                              </span>
                            )}
                            <span
                              className={cn(
                                'shrink-0 rounded-full px-2 py-0.5 text-xs font-medium',
                                lesson.isActive
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              )}
                            >
                              {lesson.isActive ? 'Active' : 'Inactive'}
                            </span>
                            <span className="shrink-0 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium capitalize text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              {lesson.lessonType}
                            </span>
                            {!lesson.termId ? (
                              <span className="shrink-0 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                Private
                              </span>
                            ) : (
                              <span className="max-w-full truncate rounded-full bg-teal-100 px-2 py-0.5 text-xs font-medium text-teal-800 dark:bg-teal-900 dark:text-teal-200">
                                {getTermName(lesson.termId)}
                              </span>
                            )}
                            {lesson.cancelledFromDate && (
                              <span
                                className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                                title={lesson.cancelledReason ? `Reason: ${lesson.cancelledReason}` : 'Term cancelled'}
                              >
                                Term cancelled
                              </span>
                            )}
                          </div>

                          {!lesson.termId && lesson.startDate && lesson.numberOfSessions ? (
                            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
                              Starts {new Date(lesson.startDate).toLocaleDateString()} · {lesson.numberOfSessions} sessions
                            </p>
                          ) : null}

                          {lesson.description && (
                            <p
                              className={cn(
                                'mt-1.5 line-clamp-2 text-xs text-gray-700 dark:text-gray-300 sm:mt-2 sm:text-sm',
                                lessonDescriptionTextClassName
                              )}
                            >
                              {lesson.description}
                            </p>
                          )}

                          <div className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-2 text-xs text-gray-600 dark:text-gray-400 sm:mt-4 sm:gap-4 sm:text-sm md:grid-cols-4">
                            <div className="col-span-2 min-w-0 sm:col-span-2 md:col-span-4">
                              <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Schedule</p>
                              <p className="mt-0.5">
                                {(lesson.daysOfWeek || [lesson.daysOfWeek])
                                  .map((d) => DAYS_OF_WEEK[d as number])
                                  .join(', ')}{' '}
                                {formatTimeHHMM(lesson.startTime)}–{formatTimeHHMM(lesson.endTime)}
                              </p>
                            </div>
                            <div className="min-w-0">
                              <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Court</p>
                              <p className="mt-0.5 truncate">
                                {getLessonCourtIds(lesson).map(getCourtName).join(', ') || '—'}
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Capacity</p>
                              <p className="mt-0.5">
                                {lesson.maxCapacity === -1 ? (
                                  `${lesson.currentEnrollment || 0} (no limit)`
                                ) : (
                                  <>
                                    {lesson.currentEnrollment || 0}/{lesson.maxCapacity}
                                    <span className="block text-muted-foreground sm:inline sm:ml-1">
                                      ({lesson.maxCapacity - (lesson.currentEnrollment || 0)} open)
                                    </span>
                                  </>
                                )}
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Price</p>
                              <p className="mt-0.5 tabular-nums">
                                {formatCurrency(lesson.price, lesson.currency || academyCurrency)}
                              </p>
                            </div>
                            {getLessonCoachIds(lesson).length > 0 && (
                              <div className="col-span-2 min-w-0">
                                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Coach</p>
                                <div className="mt-0.5 flex flex-wrap items-center gap-1">
                                  {getLessonCoachIds(lesson).map((coachId) => {
                                    const coach = getCoachById(coachId);
                                    if (!coach) {
                                      return (
                                        <span key={coachId} className="text-gray-500">
                                          Unknown
                                        </span>
                                      );
                                    }
                                    return (
                                      <span key={coachId} className="inline-flex max-w-full items-center gap-1">
                                        <UserAvatar
                                          firstName={coach.firstName}
                                          lastName={coach.lastName}
                                          size="sm"
                                        />
                                        <span className="truncate">
                                          {coach.firstName} {coach.lastName}
                                        </span>
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            {lesson.skillLevel && (
                              <div>
                                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Level</p>
                                <p className="mt-0.5 capitalize">{lesson.skillLevel}</p>
                              </div>
                            )}
                            {lesson.ageGroup && (
                              <div>
                                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Age</p>
                                <p className="mt-0.5 capitalize">{lesson.ageGroup}</p>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex justify-end border-t border-border/60 pt-2.5 sm:ml-4 sm:border-0 sm:pt-0">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={!hasValidSubscription}
                                className="h-8 gap-1.5 px-2.5"
                              >
                                <MoreVertical className="h-4 w-4" aria-hidden />
                                Actions
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {lesson.termId && !lesson.cancelledFromDate && (
                                <DropdownMenuItem
                                  onClick={() => handleOpenPostpone(lesson)}
                                  disabled={!hasValidSubscription || postponeLoading}
                                  className="text-amber-600 focus:text-amber-700"
                                >
                                  <CalendarClock className="h-4 w-4 mr-2" />
                                  Postpone a lesson
                                </DropdownMenuItem>
                              )}
                              {!lesson.termId && canQuickDuplicateLesson(lesson) && (
                                <DropdownMenuItem
                                  onClick={() => duplicateLesson(lesson)}
                                  disabled={!hasValidSubscription}
                                >
                                  <Copy className="h-4 w-4 mr-2" />
                                  Duplicate
                                </DropdownMenuItem>
                              )}
                              {!lesson.termId && (
                                <DropdownMenuItem
                                  onClick={() => copyShareableLink(lesson.id, lesson.name)}
                                  className="text-purple-600 focus:text-purple-700"
                                >
                                  <Link2 className="h-4 w-4 mr-2" />
                                  Copy Link
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={() => offerEditLesson(lesson)}
                                disabled={!hasValidSubscription}
                              >
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDelete(lesson)}
                                disabled={!hasValidSubscription}
                                className="text-red-600 focus:text-red-700"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </div>
                )}
                  />
                ) : null}

                {isSourceActive('court_hire') ? (
                  <section>
                    <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-white">
                      Court hire
                    </h3>
                    {listCourtHireOccurrences.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        No court hire bookings in this period.
                      </p>
                    ) : (
                      <ul className="space-y-2">
                        {listCourtHireOccurrences.map((occ) => (
                          <li
                            key={occ.lessonId}
                            className="rounded-lg border border-border bg-card p-3 text-sm"
                          >
                            <p className="font-medium text-gray-900 dark:text-white">
                              {occ.lessonName}
                            </p>
                            <p className="text-gray-600 dark:text-gray-400">
                              {format(occ.date, 'EEE, MMM d')} ·{' '}
                              {formatTimeHHMM(occ.startTime)} – {formatTimeHHMM(occ.endTime)}
                              {occ.courtLabel ? ` · ${occ.courtLabel}` : ''}
                            </p>
                          </li>
                        ))}
                      </ul>
                    )}
                    <Link
                      to="/admin/court-hire"
                      className="mt-3 inline-block text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
                    >
                      Manage court hire
                    </Link>
                  </section>
                ) : null}

                {isSourceActive('closure') ? (
                  <section>
                    <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-white">
                      Closures
                    </h3>
                    {listClosureOccurrences.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        No closures in this period.
                      </p>
                    ) : (
                      <ul className="space-y-2">
                        {listClosureOccurrences.map((occ) => (
                          <li
                            key={occ.lessonId}
                            className="rounded-lg border border-border bg-card p-3 text-sm"
                          >
                            <p className="font-medium text-gray-900 dark:text-white">Closure</p>
                            <p className="text-gray-600 dark:text-gray-400">
                              {format(occ.date, 'EEE, MMM d')} ·{' '}
                              {formatTimeHHMM(occ.startTime)} – {formatTimeHHMM(occ.endTime)}
                              {occ.courtLabel ? ` · ${occ.courtLabel}` : ''}
                            </p>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                ) : null}

                {isSourceActive('play_event') ? (
                  <section>
                    <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-white">
                      Play events
                    </h3>
                    {listPlayEventOccurrences.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        No play events in this period.
                      </p>
                    ) : (
                      <ul className="space-y-2">
                        {listPlayEventOccurrences.map((occ) => (
                          <li
                            key={occ.lessonId}
                            className="rounded-lg border border-border bg-card p-3 text-sm"
                          >
                            <p className="font-medium text-gray-900 dark:text-white">
                              {occ.lessonName}
                            </p>
                            <p className="text-gray-600 dark:text-gray-400">
                              {format(occ.date, 'EEE, MMM d')} ·{' '}
                              {formatTimeHHMM(occ.startTime)} – {formatTimeHHMM(occ.endTime)}
                              {occ.courtLabel ? ` · ${occ.courtLabel}` : ''}
                            </p>
                          </li>
                        ))}
                      </ul>
                    )}
                    <Link
                      to="/admin/play-events"
                      className="mt-3 inline-block text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
                    >
                      Manage play events
                    </Link>
                  </section>
                ) : null}
              </div>
            }
            previewLesson={
              showForm && selectedDaysOfWeek.length > 0 && watchedStartTime && watchedEndTime
                ? {
                  daysOfWeek: selectedDaysOfWeek,
                  startTime: watchedStartTime,
                  endTime: watchedEndTime,
                  name: watch('name') || undefined,
                }
                : undefined
            }
          />
            )
            ) : (
              <div className="flex min-h-[220px] flex-col items-center justify-center py-16">
                <div
                  className="h-10 w-10 animate-spin rounded-full border-2 border-blue-600 border-t-transparent dark:border-blue-400 dark:border-t-transparent"
                  aria-hidden
                />
                <p className="mt-4 text-sm text-muted-foreground">Loading schedule…</p>
              </div>
            )}
          </div>
      </div>

      <SessionRescheduleNotifyDialog
        open={!!pendingDayReschedule}
        lessonName={pendingDayReschedule?.occurrence.lessonName ?? 'Lesson'}
        requireReason={pendingDayRescheduleMeta?.requireReason ?? false}
        showNotifyOption={pendingDayRescheduleMeta?.timeChanged ?? true}
        loading={dayRescheduleSaving}
        onCancel={() => setPendingDayReschedule(null)}
        onConfirm={(notifyGuardians, reason) => {
          if (pendingDayReschedule) {
            void commitDayReschedule(pendingDayReschedule, notifyGuardians, reason);
          }
        }}
      />

      <LessonSessionDetailModal
        isOpen={!!sessionDetailOccurrence && !!currentAcademy?.id}
        onClose={() => setSessionDetailOccurrence(null)}
        occurrence={sessionDetailOccurrence}
        coaches={coaches}
        lessons={allLessons}
        terms={terms}
        academyId={currentAcademy?.id ?? ''}
        academySlug={currentAcademy?.slug}
        hasValidSubscription={hasValidSubscription}
        makeupLessonEnabled={currentAcademy?.makeupLessonEnabled ?? false}
        tooltipContext={calendarTooltipContext}
        academySports={academySports}
        ageGroupById={ageGroupById}
        onEdit={() => {
          if (!sessionDetailOccurrence) return;
          handleEdit(sessionDetailOccurrence.lesson);
          setSessionDetailOccurrence(null);
        }}
        onRequestPostpone={() => {
          if (!sessionDetailOccurrence?.lesson.termId) return;
          setLessonForPostpone(sessionDetailOccurrence.lesson);
          setPostponeOriginalDate(sessionDetailOccurrence.sessionDateYmd);
          setPostponeOccurrenceDates([]);
          setShowPostponeModal(true);
          setSessionDetailOccurrence(null);
        }}
        onAfterAction={refreshCalendarAfterOverride}
        onDuplicate={
          sessionDetailOccurrence && canQuickDuplicateLesson(sessionDetailOccurrence.lesson)
            ? () => {
                duplicateLesson(
                  sessionDetailOccurrence.lesson,
                  parseLocalDateYmd(sessionDetailOccurrence.sessionDateYmd) ?? undefined
                );
                setSessionDetailOccurrence(null);
              }
            : undefined
        }
      />

      <PlayEventScheduleDetailDrawer
        isOpen={!!playEventDetailOccurrence}
        onClose={() => setPlayEventDetailOccurrence(null)}
        occurrence={playEventDetailOccurrence}
        organizationId={organizationId}
        onAfterAction={refreshCalendarAfterOverride}
      />

      <LessonEditRunningScheduleWarningDialog
        open={!!pendingRunningScheduleEditLesson}
        onOpenChange={(open) => {
          if (!open) setPendingRunningScheduleEditLesson(null);
        }}
        variant={
          pendingRunningScheduleEditLesson?.lessonType === 'camp' ? 'camp' : 'term_group'
        }
        termName={
          pendingRunningScheduleEditLesson?.termId
            ? terms.find((t) => t.id === pendingRunningScheduleEditLesson.termId)?.name
            : undefined
        }
        onContinue={() => {
          if (pendingRunningScheduleEditLesson) {
            handleEdit(pendingRunningScheduleEditLesson);
          }
          setPendingRunningScheduleEditLesson(null);
        }}
      />

      <PostponeLessonModal
        isOpen={showPostponeModal}
        stackLevel="elevated"
        lesson={lessonForPostpone}
        originalDate={postponeOriginalDate}
        occurrenceDates={postponeOccurrenceDates.length > 0 ? postponeOccurrenceDates : undefined}
        isLoading={postponeLoading}
        smsAvailable={smsAvailable}
        onCancel={() => {
          setShowPostponeModal(false);
          setLessonForPostpone(null);
          setPostponeOccurrenceDates([]);
          setPostponeOriginalDate(null);
        }}
        onConfirm={handlePostponeConfirm}
      />
    </div>
  );
}
